import React, { useState, useEffect, useCallback, useRef, useMemo, useDeferredValue } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Play,
  Pause,
  Download,
  Share2,
  Youtube,
  Search,
  Copy,
  Check,
  Languages,
  Users,
  X,
  Star,
  FileText,
  List,
  Sparkles,
  Volume2,
  Volume1,
  VolumeX,
  ArrowLeft,
  Clock,
  AlertCircle,
  RefreshCw,
  RotateCcw,
  RotateCw,
  AlertTriangle,
  ChevronUp,
  ChevronDown,
  ZoomIn,
  ZoomOut,
  Minimize2,
  Maximize2,
  Image,
  FileBox,
  ExternalLink,
} from 'lucide-react';
import { Badge, Button, Card, Modal, ConfirmDialog } from '../components/UIComponents';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranscriptions, useTranscriptionPolling } from '../hooks/useTranscriptions';
import * as transcriptionsApi from '../api/transcriptions';
import { getApiErrorCode, handleApiError } from '../api/client';
import { getDownloadURL } from '../api/files';
import EditableSegment from '../components/EditableSegment';
import EditableFilename from '../components/EditableFilename';
import ManageSpeakersModal, { type SpeakerGroup } from '../components/ManageSpeakersModal';
import TranslationSettings, { type TranslationDisplayMode } from '../components/TranslationSettings';
import ExportTranscript, { type ExportOptions } from '../components/ExportTranscript';
import ShareDialog from '../components/ShareDialog';
import { generateTXT, generateJSON, generateMarkdown, generateSRT, generateVTT, downloadBlob, buildExportFilename } from '../utils/exportUtils';
import { stripKnownTranscriptionTitleExtension } from '../utils/transcriptionTitle';
import ProcessingTimer from '../components/ProcessingTimer';
import { TranscriptionSkeletonHeader, TranscriptionSkeletonBody } from '../components/TranscriptionSkeleton';
import { useSubscription } from '../hooks/useBilling';
import { MindMapViewer, MindMapViewerRef } from '../components/MindMapViewer';
import { ToolbarButton } from '../components/ToolbarButton';
import { SummaryMarkdown } from '../components/SummaryMarkdown';
import { CopyIconButton } from '../components/CopyIconButton';
import { TemplateSelect, TemplateOption } from '../components/TemplateSelect';
import {
  findAllMatches,
  groupMatchesBySegmentId,
  type SearchMatch
} from '../utils/searchUtils';
import { applyTranslationUpdates } from '../utils/translationUpdates';
import { useTransition } from '../hooks/useTransition';
import { useScrollBoundary } from '../hooks/useScrollBoundary';
import { TranscriptScrollFab } from '../components/TranscriptScrollFab';
import { useTranslation } from 'react-i18next';
import { detectLinkImportProvider, getLinkImportProviderLabel } from '../utils/linkImportPlatforms';

const EMPTY_SEARCH_MATCHES: SearchMatch[] = [];

// ============================================================================
// Sub-Components (inline for now, can extract later)
// ============================================================================

// TranscriptHeader Component
interface TranscriptHeaderProps {
  fileName: React.ReactNode;
  processingTime?: number;
  isEdited?: boolean;
  isTranslationStale?: boolean;
  onBack: () => void;
  onShare: () => void;
  sourceUrl?: string;
  onExport: () => void;
  onRetry?: () => void;
  isRetrying?: boolean;
  status?: string;
  startedAt?: string;
  createdAt?: string;
  progress?: number;
}

const TranscriptHeader: React.FC<TranscriptHeaderProps> = ({
  fileName,
  processingTime,
  isEdited,
  isTranslationStale,
  onBack,
  onShare,
  sourceUrl,
  onExport,
  onRetry,
  isRetrying,
  status,
  startedAt,
  createdAt,
  progress
}) => {
  const { t } = useTranslation();
  const formatProcessingTime = (seconds: number) => {
    if (seconds < 60) return t('transcription.processingTime.seconds', { count: Math.round(seconds) });
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    if (secs > 0) {
      return t('transcription.processingTime.minutesAndSeconds', { minutes: mins, seconds: secs });
    }
    return t('transcription.processingTime.minutes', { count: mins });
  };

  const isProcessing = ['pending', 'queued', 'processing'].includes(status || '');
  const sourceProvider = sourceUrl ? detectLinkImportProvider(sourceUrl) : null;
  const isSupportedSource = Boolean(sourceProvider);
  const queuedTimeoutMs = Number(import.meta.env.VITE_TRANSCRIPTION_QUEUED_TIMEOUT_MS) || 20 * 60 * 1000;
  const createdAtMs = createdAt ? new Date(createdAt).getTime() : NaN;
  const isStuckQueued = status === 'queued' && Number.isFinite(createdAtMs) && Date.now() - createdAtMs > queuedTimeoutMs;

  return (
    <div className="flex items-center justify-between py-1 px-1 mb-1">
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-1 hover:bg-slate-100 dark:hover:bg-zinc-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-slate-600 dark:text-zinc-400" />
        </button>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-slate-900 dark:text-white">{fileName}</h1>
             {isEdited && <Badge variant="info">{t('transcription.badges.edited')}</Badge>}
            {isTranslationStale && (
              <Badge variant="warning">
                <span className="flex items-center gap-1">
                   <AlertTriangle className="h-3 w-3" />
                   {t('transcription.badges.translationOutdated')}
                </span>
              </Badge>
            )}
             {status === 'queued' && <Badge variant="warning">{t('transcription.status.queued')}</Badge>}
             {isStuckQueued && (
               <Badge variant="warning">
                 <span className="flex items-center gap-1">
                   <AlertTriangle className="h-3 w-3" />
                   {t('transcription.badges.stuckInQueue')}
                 </span>
               </Badge>
             )}
             {status === 'pending' && <Badge variant="neutral">{t('transcription.badges.pending')}</Badge>}
             {status === 'processing' && (
               <Badge variant="primary">
                 {progress
                   ? t('transcription.badges.processingWithProgress', { percent: Math.round(progress) })
                   : t('transcription.status.processing')}
               </Badge>
             )}
          </div>
          {isProcessing && createdAt ? (
            <div className="mt-1">
              <ProcessingTimer 
                startedAt={startedAt} 
                createdAt={createdAt} 
              />
            </div>
          ) : processingTime ? (
            <div className="flex items-center gap-1 text-sm text-slate-500 dark:text-zinc-500">
              <Clock className="h-3.5 w-3.5" />
               <span>{t('transcription.processingTime.label', { time: formatProcessingTime(processingTime) })}</span>
            </div>
          ) : null}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {status === 'queued' && onRetry && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRetry}
            disabled={Boolean(isRetrying)}
             title={t('transcription.toolbar.requeueTitle')}
          >
            <RefreshCw className={`h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`} />
            <span className="ms-2">{t('transcription.toolbar.requeue')}</span>
          </Button>
        )}
        {isSupportedSource && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.open(sourceUrl, '_blank', 'noopener,noreferrer')}
            title={`Open original ${getLinkImportProviderLabel(sourceProvider)} link`}
            aria-label={`Open original ${getLinkImportProviderLabel(sourceProvider)} link`}
          >
            {sourceProvider === 'youtube' ? (
              <Youtube className="h-4 w-4 text-red-600 dark:text-red-400" />
            ) : (
              <ExternalLink className="h-4 w-4" />
            )}
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={onShare} disabled={isProcessing}>
          <Share2 className="h-4 w-4" />
        </Button>
        <Button variant="primary" size="sm" onClick={onExport} disabled={isProcessing}>
           <Download className="h-4 w-4 me-2" />
           {t('transcription.toolbar.exportTranscript')}
        </Button>
      </div>
    </div>
  );
};

// TranscriptToolbar Component
interface TranscriptToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onCopyAll: () => void;
  showSpeakerButton: boolean;
  isSpeakerSettingsOpen: boolean;
  onToggleSpeakerSettingsOpen: () => void;
  speakerGroups: SpeakerGroup[];
  onRenameSpeaker: (segmentId: number, newSpeaker: string) => Promise<boolean>;
  isEdited: boolean;
  onRevert: () => void;
  isReverting?: boolean;
  isTranslationOpen: boolean;
  onToggleTranslationOpen: () => void;
  translationSettings: {
    targetLanguage: string;
    onTargetLanguageChange: (value: string) => void;
    displayMode: TranslationDisplayMode;
    onDisplayModeChange: (mode: TranslationDisplayMode) => void;
    onTranslate: () => void;
    isTranslating?: boolean;
    translationProgress?: number;
    hasTranslation?: boolean;
    isTranslationStale?: boolean;
    errorMessage?: string | null;
    languageOptions: Array<{ value: string; label: string }>;
  };
  // Search navigation props
  matchCount: number;
  currentMatchIndex: number;
  onNextMatch: () => void;
  onPrevMatch: () => void;
  onClearSearch: () => void;
}

const TranscriptToolbar: React.FC<TranscriptToolbarProps> = ({
  searchValue,
  onSearchChange,
  onCopyAll,
  showSpeakerButton,
  isSpeakerSettingsOpen,
  onToggleSpeakerSettingsOpen,
  speakerGroups,
  onRenameSpeaker,
  isEdited,
  onRevert,
  isReverting = false,
  isTranslationOpen,
  onToggleTranslationOpen,
  translationSettings,
  matchCount,
  currentMatchIndex,
  onNextMatch,
  onPrevMatch,
  onClearSearch
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const speakerRef = useRef<HTMLDivElement>(null);
  const translationRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  const speakerTransition = useTransition(isSpeakerSettingsOpen, 200);
  const translationTransition = useTransition(isTranslationOpen, 200);

  useEffect(() => {
    if (!isTranslationOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!translationRef.current) return;
      if (!translationRef.current.contains(event.target as Node)) {
        onToggleTranslationOpen();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isTranslationOpen, onToggleTranslationOpen]);

  useEffect(() => {
    if (!isSpeakerSettingsOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!speakerRef.current) return;
      if (!speakerRef.current.contains(event.target as Node)) {
        onToggleSpeakerSettingsOpen();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isSpeakerSettingsOpen, onToggleSpeakerSettingsOpen]);

  const handleCopy = () => {
    onCopyAll();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) {
        onPrevMatch();
      } else {
        onNextMatch();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClearSearch();
      searchInputRef.current?.blur();
    }
  };

  const hasSearch = searchValue.trim() !== '';

  return (
    <div className="flex items-center justify-between px-4 py-1.5 border-b border-slate-200 dark:border-zinc-700">
       <h2 className="text-base font-semibold text-slate-900 dark:text-white">{t('transcription.toolbar.transcriptTitle')}</h2>
      <div className="flex items-center gap-1">
        <div className="relative flex items-center">
           <Search className="absolute start-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-zinc-500" />
           <input
            ref={searchInputRef}
            type="text"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={handleSearchKeyDown}
             placeholder={t('transcription.toolbar.searchPlaceholder')}
             className={`ps-8 py-1.5 text-sm border border-transparent rounded-md focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600 transition-all bg-transparent hover:bg-slate-50 dark:hover:bg-zinc-800 focus:bg-white dark:focus:bg-zinc-900 dark:text-white dark:placeholder-zinc-500 ${
               hasSearch ? 'w-48 pe-24' : 'w-32 pe-3 focus:w-48'
             }`}
           />
           {hasSearch && (
             <div className="absolute end-1 flex items-center gap-0.5">
               <span className="text-xs text-slate-500 dark:text-zinc-500 me-1">
                 {matchCount > 0
                   ? t('transcription.toolbar.searchCount', { current: currentMatchIndex + 1, total: matchCount })
                   : t('transcription.toolbar.searchNoResults')}
               </span>
              <button
                onClick={onPrevMatch}
                disabled={matchCount === 0}
                className="p-0.5 hover:bg-slate-200 dark:hover:bg-zinc-700 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                 title={t('transcription.toolbar.prevMatchTitle')}
              >
                <ChevronUp className="h-3.5 w-3.5 text-slate-600 dark:text-zinc-400" />
              </button>
              <button
                onClick={onNextMatch}
                disabled={matchCount === 0}
                className="p-0.5 hover:bg-slate-200 dark:hover:bg-zinc-700 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                 title={t('transcription.toolbar.nextMatchTitle')}
              >
                <ChevronDown className="h-3.5 w-3.5 text-slate-600 dark:text-zinc-400" />
              </button>
              <button
                onClick={onClearSearch}
                className="p-0.5 hover:bg-slate-200 dark:hover:bg-zinc-700 rounded transition-colors"
                 title={t('transcription.toolbar.clearSearchTitle')}
              >
                <X className="h-3.5 w-3.5 text-slate-600 dark:text-zinc-400" />
              </button>
            </div>
          )}
        </div>
        {showSpeakerButton && (
          <div className="relative" ref={speakerRef}>
            <button
              type="button"
              className={`p-2 hover:bg-slate-100 dark:hover:bg-zinc-800 rounded-lg transition-colors ${
                isSpeakerSettingsOpen ? 'bg-slate-100 dark:bg-zinc-800' : ''
              }`}
               title={t('transcription.toolbar.speakerIdentificationTitle')}
              onClick={(event) => {
                event.stopPropagation();
                onToggleSpeakerSettingsOpen();
              }}
            >
              <Users className="h-4 w-4 text-slate-500 dark:text-zinc-400" />
            </button>
            {speakerTransition.shouldMount && (
              <div className={`absolute right-0 top-full mt-2 z-20 ${speakerTransition.isVisible ? 'dropdown-in' : 'dropdown-out'}`}>
                <ManageSpeakersModal
                  speakerGroups={speakerGroups}
                  onRenameSpeaker={onRenameSpeaker}
                />
              </div>
            )}
          </div>
        )}
        <button
          className={`p-2 hover:bg-slate-100 dark:hover:bg-zinc-800 rounded-lg transition-colors ${copied ? 'text-green-600 dark:text-green-400' : ''}`}
           title={copied ? t('common.copied') : t('transcription.toolbar.copyAll')}
          onClick={handleCopy}
        >
          {copied ? (
            <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
          ) : (
            <Copy className="h-4 w-4 text-slate-500 dark:text-zinc-400" />
          )}
        </button>
        <div className="relative" ref={translationRef}>
          <button
            className={`p-2 hover:bg-slate-100 dark:hover:bg-zinc-800 rounded-lg transition-colors ${
              isTranslationOpen ? 'bg-slate-100 dark:bg-zinc-800' : ''
            } ${translationSettings.isTranslating ? 'translate-shimmer' : ''}`}
            title={
              translationSettings.isTranslating
                ? t('transcription.translation.inProgressTitle')
                : t('transcription.translation.title')
            }
            onClick={(event) => {
              event.stopPropagation();
              onToggleTranslationOpen();
            }}
          >
            <Languages className={`h-4 w-4 ${
              translationSettings.isTranslating ? 'text-indigo-500' : 'text-slate-500 dark:text-zinc-400'
            }`} />
          </button>
          {translationTransition.shouldMount && (
            <div className={`absolute right-0 top-full mt-2 z-20 ${translationTransition.isVisible ? 'dropdown-in' : 'dropdown-out'}`}>
              <TranslationSettings {...translationSettings} />
            </div>
          )}
        </div>
        {isEdited && (
          <button
            className="flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-slate-600 dark:text-zinc-400 hover:bg-slate-100 dark:hover:bg-zinc-800 rounded-md transition-colors disabled:opacity-60"
            title={t('transcription.toolbar.revertAllEditsTitle')}
            onClick={onRevert}
            disabled={isReverting}
          >
            <RotateCcw className={`h-3.5 w-3.5 ${isReverting ? 'animate-spin' : ''}`} />
            {t('transcription.toolbar.revertConfirm')}
          </button>
        )}
      </div>
    </div>
  );
};

// QualityRating Component
interface QualityRatingProps {
  onDismiss: () => void;
}

const QualityRating: React.FC<QualityRatingProps> = ({ onDismiss }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-zinc-800/50 border-b border-slate-200 dark:border-zinc-700">
      <div className="flex items-center gap-3">
        <span className="text-sm text-slate-600 dark:text-zinc-400">{t('transcription.toolbar.ratingTitle')}</span>
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
              onClick={() => setRating(star)}
              className="p-0.5 transition-colors"
            >
              <Star
                className={`h-5 w-5 ${
                  star <= (hoveredRating || rating)
                    ? 'text-amber-400 fill-amber-400'
                    : 'text-slate-300 dark:text-zinc-600'
                }`}
              />
            </button>
          ))}
        </div>
      </div>
      <button
        onClick={onDismiss}
        className="p-1 hover:bg-slate-200 dark:hover:bg-zinc-700 rounded transition-colors"
      >
        <X className="h-4 w-4 text-slate-400 dark:text-zinc-500" />
      </button>
    </div>
  );
};

// UpgradeBanner Component
interface UpgradeBannerProps {
  onDismiss?: () => void;
}

const UpgradeBanner: React.FC<UpgradeBannerProps> = ({ onDismiss }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="relative flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-600 text-white rounded-lg">
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0 h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
          <Sparkles className="h-4 w-4" />
        </div>
        <div>
          <span className="text-sm font-medium">{t('transcription.upgrade.title')}</span>
          <p className="text-xs text-white/70">{t('transcription.upgrade.description')}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="bg-white text-indigo-600 border-white hover:bg-indigo-50"
          onClick={() => navigate('/pricing')}
        >
          {t('transcription.upgrade.action')}
        </Button>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            aria-label={t('common.close')}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

// Custom Skip 15 seconds icons
const Skip15Back: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`relative ${className}`}>
    <RotateCcw className="h-full w-full" />
    <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold" style={{ marginTop: '1px' }}>
      15
    </span>
  </div>
);

const Skip15Forward: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`relative ${className}`}>
    <RotateCw className="h-full w-full" />
    <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold" style={{ marginTop: '1px' }}>
      15
    </span>
  </div>
);

// AudioPlayerBar Component
interface AudioPlayerBarProps {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  playbackSpeed: number;
  volume: number;
  disabled?: boolean;
  onPlayPause: () => void;
  onSkipBack: () => void;
  onSkipForward: () => void;
  onSeek: (time: number) => void;
  onSpeedChange: (speed: number) => void;
  onVolumeChange: (volume: number) => void;
}

const AudioPlayerBar: React.FC<AudioPlayerBarProps> = ({
  isPlaying,
  currentTime,
  duration,
  playbackSpeed,
  volume,
  disabled = false,
  onPlayPause,
  onSkipBack,
  onSkipForward,
  onSeek,
  onSpeedChange,
  onVolumeChange
}) => {
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const speedOptions = [2, 1.75, 1.5, 1.25, 1, 0.75, 0.5, 0.25];
  const { t } = useTranslation();
  const volumeRef = useRef<HTMLDivElement>(null);
  const speedRef = useRef<HTMLDivElement>(null);

  // Close popups when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (volumeRef.current && !volumeRef.current.contains(event.target as Node)) {
        setShowVolumeSlider(false);
      }
      if (speedRef.current && !speedRef.current.contains(event.target as Node)) {
        setShowSpeedMenu(false);
      }
    };

    if (showVolumeSlider || showSpeedMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showVolumeSlider, showSpeedMenu]);

  // Get the appropriate volume icon based on volume level
  const VolumeIcon = volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Disabled state: show message that audio is not available
  if (disabled) {
    return (
      <div className="flex items-center justify-center gap-3 px-4 py-3 bg-slate-50 dark:bg-zinc-800 border-t border-slate-200 dark:border-zinc-700">
        <VolumeX className="h-5 w-5 text-slate-400 dark:text-zinc-500" />
        <span className="text-sm text-slate-500 dark:text-zinc-400">{t('transcription.audio.deleted')}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4 px-4 py-3 bg-white dark:bg-zinc-900 border-t border-slate-200 dark:border-zinc-700">
      {/* Playback Controls */}
      <div className="flex items-center gap-2">
        <button
          onClick={onSkipBack}
          className="p-2 hover:bg-slate-100 dark:hover:bg-zinc-800 rounded-full transition-colors"
          title={t('transcription.toolbar.skipBack', { seconds: 15 })}
        >
          <Skip15Back className="h-5 w-5 text-slate-600 dark:text-zinc-400" />
        </button>
        <button
          onClick={onPlayPause}
          className="p-3 bg-indigo-600 hover:bg-indigo-700 rounded-full play-button"
        >
          <div className="h-5 w-5 flex items-center justify-center">
            {isPlaying ? (
              <Pause className="h-5 w-5 text-white" fill="white" />
            ) : (
              <Play className="h-5 w-5 text-white" fill="white" style={{ marginLeft: '2px' }} />
            )}
          </div>
        </button>
        <button
          onClick={onSkipForward}
          className="p-2 hover:bg-slate-100 dark:hover:bg-zinc-800 rounded-full transition-colors"
          title={t('transcription.toolbar.skipForward', { seconds: 15 })}
        >
          <Skip15Forward className="h-5 w-5 text-slate-600 dark:text-zinc-400" />
        </button>
      </div>

      {/* Time & Progress */}
      <span className="text-xs text-slate-500 dark:text-zinc-500 font-mono w-10">{formatTime(currentTime)}</span>

      <div
        className="flex-1 h-1.5 bg-slate-200 dark:bg-zinc-700 rounded-full cursor-pointer group"
        onClick={(e) => {
          // Don't seek if duration is invalid
          if (!Number.isFinite(duration) || duration <= 0) {
            return;
          }
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const percent = x / rect.width;
          onSeek(percent * duration);
        }}
      >
        <div
          className="h-full bg-indigo-600 rounded-full relative"
          style={{ width: `${progress}%` }}
        >
          <div className="absolute right-0 top-1/2 -translate-y-1/2 h-3 w-3 bg-indigo-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>

      <span className="text-xs text-slate-500 dark:text-zinc-500 font-mono w-10">{formatTime(duration)}</span>

      {/* Volume Control */}
      <div className="relative" ref={volumeRef}>
        <button
          onClick={() => setShowVolumeSlider(!showVolumeSlider)}
          className="p-2 hover:bg-slate-100 dark:hover:bg-zinc-800 rounded-full transition-colors"
          title={volume === 0 ? t('transcription.audio.unmute') : t('transcription.audio.mute')}
        >
          <VolumeIcon className="h-4 w-4 text-slate-600 dark:text-zinc-400" />
        </button>
        {showVolumeSlider && (
          <div className="absolute bottom-full right-0 mb-2 bg-white dark:bg-zinc-800 rounded-lg shadow-lg border border-slate-200 dark:border-zinc-700 p-3 min-w-[140px] z-10 popup-animate">
            <div className="flex items-center gap-2">
              <button
                onClick={() => onVolumeChange(volume === 0 ? 1 : 0)}
                className="p-1 hover:bg-slate-100 dark:hover:bg-zinc-700 rounded transition-colors"
                title={volume === 0 ? t('transcription.audio.unmute') : t('transcription.audio.mute')}
              >
                <VolumeIcon className="h-4 w-4 text-slate-600 dark:text-zinc-400" />
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
                className="flex-1 h-1.5 bg-slate-200 dark:bg-zinc-700 rounded-full appearance-none cursor-pointer accent-indigo-600"
              />
              <span className="text-xs text-slate-500 dark:text-zinc-400 font-mono w-8 text-right">
                {Math.round(volume * 100)}%
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Speed Control */}
      <div className="relative" ref={speedRef}>
        <button
          onClick={() => setShowSpeedMenu(!showSpeedMenu)}
          className="px-2 py-1 text-xs font-medium text-slate-600 dark:text-zinc-400 hover:bg-slate-100 dark:hover:bg-zinc-800 rounded transition-colors"
        >
          {playbackSpeed}x
        </button>
        {showSpeedMenu && (
          <div className="absolute bottom-full right-0 mb-2 bg-white dark:bg-zinc-800 rounded-lg shadow-lg border border-slate-200 dark:border-zinc-700 py-1 min-w-[80px] z-10 popup-animate">
            {speedOptions.map((speed) => (
              <button
                key={speed}
                onClick={() => {
                  onSpeedChange(speed);
                  setShowSpeedMenu(false);
                }}
                className={`w-full px-3 py-1.5 text-xs text-left hover:bg-slate-50 dark:hover:bg-zinc-700 transition-colors ${
                  playbackSpeed === speed ? 'text-indigo-600 dark:text-indigo-400 font-medium bg-indigo-50 dark:bg-indigo-900/30' : 'text-slate-600 dark:text-zinc-400'
                }`}
              >
                {speed}x
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// AIInsightsPanel Component
interface AIInsightsPanelProps {
  activeTab: 'overview' | 'keypoints' | 'mindmap';
  onTabChange: (tab: 'overview' | 'keypoints' | 'mindmap') => void;
  summaryTemplates: transcriptionsApi.SummaryTemplate[];
  selectedSummaryTemplateId: string;
  onSelectSummaryTemplateId: (templateId: string) => void;
  onCreateSummaryTemplate: (name: string, prompt: string) => Promise<void>;
  onUpdateSummaryTemplate: (templateId: string, name: string, prompt: string) => Promise<void>;
  onDeleteSummaryTemplate: (templateId: string) => Promise<void>;
  summaryStatus: SummaryUIStatus;
  summaryMarkdown: string | null;
  summaryError: string | null;
  onGenerateSummary: () => void;
  keypointsStatus: KeypointsUIStatus;
  keypointsItems: transcriptionsApi.KeypointsItem[];
  keypointsError: string | null;
  onGenerateKeyPoints: () => void;
  mindmapStatus: MindmapUIStatus;
  mindmapMarkdown: string | null;
  mindmapError: string | null;
  onGenerateMindmap: () => void;
  // New completion indicators
  summaryNewlyCompleted: boolean;
  keypointsNewlyCompleted: boolean;
  mindmapNewlyCompleted: boolean;
}

type MindmapUIStatus = 'idle' | transcriptionsApi.MindmapStatus;
type SummaryUIStatus = 'idle' | transcriptionsApi.SummaryStatus;
type KeypointsUIStatus = 'idle' | transcriptionsApi.KeypointsStatus;

const AIInsightsPanel: React.FC<AIInsightsPanelProps> = ({
  activeTab,
  onTabChange,
  summaryTemplates,
  selectedSummaryTemplateId,
  onSelectSummaryTemplateId,
  onCreateSummaryTemplate,
  onUpdateSummaryTemplate,
  onDeleteSummaryTemplate,
  summaryStatus,
  summaryMarkdown,
  summaryError,
  onGenerateSummary,
  keypointsStatus,
  keypointsItems,
  keypointsError,
  onGenerateKeyPoints,
  mindmapStatus,
  mindmapMarkdown,
  mindmapError,
  onGenerateMindmap,
  summaryNewlyCompleted,
  keypointsNewlyCompleted,
  mindmapNewlyCompleted,
}) => {
  const { t } = useTranslation();
  const mindmapRef = useRef<MindMapViewerRef>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const mindmapViewer = useMemo(() => {
    if (!mindmapMarkdown) return null;
    return <MindMapViewer ref={mindmapRef} markdown={mindmapMarkdown} />;
  }, [mindmapMarkdown]);

  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<transcriptionsApi.SummaryTemplate | null>(null);
  const [deleteConfirmTemplate, setDeleteConfirmTemplate] = useState<transcriptionsApi.SummaryTemplate | null>(null);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplatePrompt, setNewTemplatePrompt] = useState('');
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);

  const canSelectTemplate = summaryTemplates.length > 0;
  const selectedTemplate = summaryTemplates.find(t => t.id === selectedSummaryTemplateId) || null;
  const keypointsCopyText = keypointsItems
    .map((it, idx) => `Q${idx + 1}: ${it.question}\nA${idx + 1}: ${it.answer}`)
    .join('\n\n');

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExportMenu]);

  const handleCreateTemplate = async () => {
    setTemplateError(null);
    setTemplateSaving(true);
    try {
      await onCreateSummaryTemplate(newTemplateName, newTemplatePrompt);
      setIsCreatingTemplate(false);
      setNewTemplateName('');
      setNewTemplatePrompt('');
    } catch (err) {
      setTemplateError(handleApiError(err));
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleEditTemplate = (template: TemplateOption) => {
    setEditingTemplate(template as transcriptionsApi.SummaryTemplate);
    setNewTemplateName(template.name);
    setNewTemplatePrompt(template.prompt);
    setIsCreatingTemplate(false);
    setTemplateError(null);
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;
    setTemplateError(null);
    setTemplateSaving(true);
    try {
      await onUpdateSummaryTemplate(editingTemplate.id, newTemplateName, newTemplatePrompt);
      setEditingTemplate(null);
      setNewTemplateName('');
      setNewTemplatePrompt('');
    } catch (err) {
      setTemplateError(handleApiError(err));
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!deleteConfirmTemplate) return;
    setTemplateSaving(true);
    try {
      await onDeleteSummaryTemplate(deleteConfirmTemplate.id);
      setDeleteConfirmTemplate(null);
    } catch (err) {
      setTemplateError(handleApiError(err));
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingTemplate(null);
    setIsCreatingTemplate(false);
    setNewTemplateName('');
    setNewTemplatePrompt('');
    setTemplateError(null);
  };

  const handleExportPNG = async () => {
    setShowExportMenu(false);
    await mindmapRef.current?.exportPNG();
  };

  const handleExportMarkdown = () => {
    setShowExportMenu(false);
    mindmapRef.current?.exportMarkdown();
  };

  const handleExportXmind = () => {
    setShowExportMenu(false);
    mindmapRef.current?.exportXmind();
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-zinc-900 rounded-lg border border-slate-200 dark:border-zinc-700 shadow-sm overflow-hidden">
      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200 dark:border-zinc-700 px-3 py-2">
        <button
          onClick={() => onTabChange('overview')}
          className={`relative px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
            activeTab === 'overview'
              ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400'
              : 'bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400 hover:bg-slate-200 dark:hover:bg-zinc-700'
          } ${(summaryStatus === 'pending' || summaryStatus === 'processing') ? 'translate-shimmer' : ''}`}
          type="button"
        >
          {(summaryStatus === 'pending' || summaryStatus === 'processing') && (
            <RefreshCw className="h-3 w-3 animate-spin" />
          )}
          {t('transcription.aiInsights.tabs.overview')}
          {summaryNewlyCompleted && activeTab !== 'overview' && (
            <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-zinc-900" />
          )}
        </button>
        <button
          onClick={() => onTabChange('keypoints')}
          className={`relative px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
            activeTab === 'keypoints'
              ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400'
              : 'bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400 hover:bg-slate-200 dark:hover:bg-zinc-700'
          } ${(keypointsStatus === 'pending' || keypointsStatus === 'processing') ? 'translate-shimmer' : ''}`}
          type="button"
        >
          {(keypointsStatus === 'pending' || keypointsStatus === 'processing') && (
            <RefreshCw className="h-3 w-3 animate-spin" />
          )}
          {t('transcription.aiInsights.tabs.keypoints')}
          {keypointsNewlyCompleted && activeTab !== 'keypoints' && (
            <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-zinc-900" />
          )}
        </button>
        <button
          onClick={() => onTabChange('mindmap')}
          className={`relative px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
            activeTab === 'mindmap'
              ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400'
              : 'bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400 hover:bg-slate-200 dark:hover:bg-zinc-700'
          } ${(mindmapStatus === 'pending' || mindmapStatus === 'processing') ? 'translate-shimmer' : ''}`}
          type="button"
        >
          {(mindmapStatus === 'pending' || mindmapStatus === 'processing') && (
            <RefreshCw className="h-3 w-3 animate-spin" />
          )}
          {t('transcription.aiInsights.tabs.mindmap')}
          {mindmapNewlyCompleted && activeTab !== 'mindmap' && (
            <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-zinc-900" />
          )}
        </button>
      </div>

      {/* Content */}
      {activeTab === 'overview' && (
        <div className="flex-1 overflow-hidden p-4 tab-fade-slide flex flex-col min-h-0">
          <div className="flex flex-col min-h-0 flex-1">
            {/* Summary Section */}
            <div className="flex flex-col min-h-0 flex-1">
              <div className="flex items-center justify-between gap-3 mb-4">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t('transcription.aiInsights.summary.title')}</h3>
                <div className="flex items-center gap-2">
                  {canSelectTemplate && (
                    <TemplateSelect
                      options={summaryTemplates}
                      value={selectedSummaryTemplateId}
                      onChange={onSelectSummaryTemplateId}
                      onAdd={() => {
                        setIsCreatingTemplate(prev => !prev);
                        setEditingTemplate(null);
                        setNewTemplateName('');
                        setNewTemplatePrompt('');
                        setTemplateError(null);
                      }}
                      onEdit={handleEditTemplate}
                      onDelete={(t) => setDeleteConfirmTemplate(t as transcriptionsApi.SummaryTemplate)}
                    />
                  )}
                  {summaryStatus === 'completed' && summaryMarkdown && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={onGenerateSummary}
                        title={t('transcription.aiInsights.summary.regenerateTitle')}
                        type="button"
                      >
                        <RefreshCw className="h-5 w-5" />
                      </Button>
                      <CopyIconButton text={summaryMarkdown} title={t('transcription.aiInsights.summary.copyTitle')} />
                    </>
                  )}
                </div>
              </div>

              {(isCreatingTemplate || editingTemplate) && (
                <div className="mb-4 rounded-lg border border-slate-200 dark:border-zinc-700 bg-slate-50 dark:bg-zinc-800/50 p-3">
                  <div className="grid gap-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-600 dark:text-zinc-400">
                        {editingTemplate ? t('transcription.aiInsights.summary.template.edit') : t('transcription.aiInsights.summary.template.new')}
                      </span>
                    </div>
                    <input
                      className="h-9 rounded-md border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 text-sm dark:text-white"
                      placeholder={t('transcription.aiInsights.summary.template.namePlaceholder')}
                      value={newTemplateName}
                      onChange={(e) => setNewTemplateName(e.target.value)}
                    />
                    <textarea
                      className="min-h-[90px] rounded-md border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm dark:text-white"
                      placeholder={t('transcription.aiInsights.summary.template.promptPlaceholder')}
                      value={newTemplatePrompt}
                      onChange={(e) => setNewTemplatePrompt(e.target.value)}
                    />
                    {templateError && <p className="text-xs text-rose-600 dark:text-rose-400">{templateError}</p>}
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancelEdit}
                        type="button"
                      >
                        {t('transcription.aiInsights.summary.template.cancel')}
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={templateSaving || !newTemplateName.trim() || !newTemplatePrompt.trim()}
                        onClick={editingTemplate ? handleUpdateTemplate : handleCreateTemplate}
                        type="button"
                      >
                        {templateSaving ? t('transcription.aiInsights.summary.template.saving') : t('transcription.aiInsights.summary.template.save')}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {summaryStatus === 'idle' && (
                <div className="flex flex-1 flex-col items-center justify-center py-8 text-center">
                  <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
                    <FileText className="h-8 w-8 text-slate-400 dark:text-zinc-500" />
                  </div>
                  <p className="text-sm text-slate-500 dark:text-zinc-400 max-w-[260px] mb-4">
                    {selectedTemplate ? selectedTemplate.prompt : t('transcription.aiInsights.summary.idle.noTemplate')}
                  </p>
                  <Button variant="primary" size="sm" onClick={onGenerateSummary} type="button">
                    <Sparkles className="h-4 w-4 me-2" />
                    {t('transcription.aiInsights.summary.idle.generate')}
                  </Button>
                </div>
              )}

              {(summaryStatus === 'pending' || summaryStatus === 'processing') && (
                <div className="flex flex-1 flex-col items-center justify-center py-8 text-center">
                  <div className="relative mb-4">
                    <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center">
                      <RefreshCw className="h-8 w-8 text-indigo-600 dark:text-indigo-400 animate-spin" />
                    </div>
                    <div className="absolute inset-0 rounded-2xl bg-indigo-100/50 dark:bg-indigo-500/20 animate-pulse" />
                  </div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">{t('transcription.aiInsights.summary.processing.title')}</p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-zinc-400">{t('transcription.aiInsights.summary.processing.description')}</p>
                </div>
              )}

              {summaryStatus === 'failed' && (
                <div className="flex flex-1 flex-col items-center justify-center py-8 text-center">
                  <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
                    <AlertTriangle className="h-8 w-8 text-slate-400 dark:text-zinc-500" />
                  </div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white mb-1">{t('transcription.aiInsights.summary.failed.title')}</p>
                  <p className="text-xs text-slate-500 dark:text-zinc-400 max-w-[280px] mb-4">{summaryError || t('transcription.aiInsights.summary.failed.fallbackError')}</p>
                  <Button variant="primary" size="sm" onClick={onGenerateSummary} type="button">
                    <RefreshCw className="h-4 w-4 me-2" />
                    {t('transcription.aiInsights.summary.failed.retry')}
                  </Button>
                </div>
              )}

              {summaryStatus === 'completed' && summaryMarkdown && (
                <div className="flex-1 min-h-0">
                  <SummaryMarkdown markdown={summaryMarkdown} />
                </div>
              )}

              {summaryStatus === 'completed' && !summaryMarkdown && (
                <div className="flex flex-1 flex-col items-center justify-center py-8 text-center">
                  <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
                    <AlertTriangle className="h-8 w-8 text-slate-400 dark:text-zinc-500" />
                  </div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white mb-1">{t('transcription.aiInsights.summary.empty.title')}</p>
                  <p className="text-xs text-slate-500 dark:text-zinc-400 max-w-[280px] mb-4">{t('transcription.aiInsights.summary.empty.description')}</p>
                  <Button variant="primary" size="sm" onClick={onGenerateSummary} type="button">
                    <RefreshCw className="h-4 w-4 me-2" />
                    {t('transcription.aiInsights.summary.empty.generate')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'keypoints' && (
        <div className="flex-1 overflow-y-auto p-4 tab-fade-slide">
          <div className="rounded-lg border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t('transcription.aiInsights.keypoints.title')}</h3>
              <div className="flex items-center gap-2">
                {keypointsStatus === 'completed' && keypointsItems.length > 0 && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={onGenerateKeyPoints}
                      title={t('transcription.aiInsights.keypoints.regenerateTitle')}
                      type="button"
                    >
                      <RefreshCw className="h-5 w-5" />
                    </Button>
                    <CopyIconButton text={keypointsCopyText} title={t('transcription.aiInsights.keypoints.copyTitle')} />
                  </>
                )}
                {(keypointsStatus === 'idle' || keypointsStatus === 'failed') && (
                  <Button variant="primary" size="sm" onClick={onGenerateKeyPoints} type="button">
                    <Sparkles className="h-4 w-4 me-2" />
                    {t('transcription.aiInsights.keypoints.generate')}
                  </Button>
                )}
              </div>
            </div>

            {keypointsStatus === 'idle' && (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
                  <List className="h-8 w-8 text-slate-400 dark:text-zinc-500" />
                </div>
                <p className="text-sm text-slate-500 dark:text-zinc-400 max-w-[320px]">
                  {t('transcription.aiInsights.keypoints.idleDescription')}
                </p>
              </div>
            )}

            {(keypointsStatus === 'pending' || keypointsStatus === 'processing') && (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="relative mb-4">
                  <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center">
                    <RefreshCw className="h-8 w-8 text-indigo-600 dark:text-indigo-400 animate-spin" />
                  </div>
                  <div className="absolute inset-0 rounded-2xl bg-indigo-100/50 dark:bg-indigo-500/20 animate-pulse" />
                </div>
                <p className="text-sm font-medium text-slate-900 dark:text-white">{t('transcription.aiInsights.keypoints.processing.title')}</p>
                <p className="mt-1 text-xs text-slate-500 dark:text-zinc-400">{t('transcription.aiInsights.keypoints.processing.description')}</p>
              </div>
            )}

            {keypointsStatus === 'failed' && (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
                  <AlertTriangle className="h-8 w-8 text-slate-400 dark:text-zinc-500" />
                </div>
                <p className="text-sm font-medium text-slate-900 dark:text-white mb-1">{t('transcription.aiInsights.keypoints.failed.title')}</p>
                <p className="text-xs text-slate-500 dark:text-zinc-400 max-w-[320px] mb-4">{keypointsError || t('transcription.aiInsights.keypoints.failed.fallbackError')}</p>
                <Button variant="primary" size="sm" onClick={onGenerateKeyPoints} type="button">
                  <RefreshCw className="h-4 w-4 me-2" />
                  {t('transcription.aiInsights.keypoints.failed.retry')}
                </Button>
              </div>
            )}

            {keypointsStatus === 'completed' && keypointsItems.length > 0 && (
              <div className="space-y-3">
                {keypointsItems.map((it, idx) => (
                  <div key={idx} className="rounded-lg border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white mb-2">{it.question}</p>
                    <p className="text-sm text-slate-600 dark:text-zinc-400 whitespace-pre-wrap">{it.answer}</p>
                  </div>
                ))}
              </div>
            )}

            {keypointsStatus === 'completed' && keypointsItems.length === 0 && (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
                  <AlertTriangle className="h-8 w-8 text-slate-400 dark:text-zinc-500" />
                </div>
                <p className="text-sm font-medium text-slate-900 dark:text-white mb-1">{t('transcription.aiInsights.keypoints.empty.title')}</p>
                <p className="text-xs text-slate-500 dark:text-zinc-400 max-w-[320px] mb-4">{t('transcription.aiInsights.keypoints.empty.description')}</p>
                <Button variant="primary" size="sm" onClick={onGenerateKeyPoints} type="button">
                  <RefreshCw className="h-4 w-4 me-2" />
                  {t('transcription.aiInsights.keypoints.empty.generate')}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'mindmap' && (
        <div className="relative flex-1 overflow-hidden tab-fade-slide">
          {(mindmapStatus === 'pending' || mindmapStatus === 'processing') && (
            <div className="flex h-full w-full flex-col items-center justify-center px-6 text-center">
              <div className="relative mb-4">
                <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center">
                  <RefreshCw className="h-8 w-8 text-indigo-600 dark:text-indigo-400 animate-spin" />
                </div>
                <div className="absolute inset-0 rounded-2xl bg-indigo-100/50 dark:bg-indigo-500/20 animate-pulse" />
              </div>
              <p className="text-sm font-medium text-slate-900 dark:text-white">{t('transcription.aiInsights.mindmap.processing.title')}</p>
              <p className="mt-1 text-xs text-slate-500 dark:text-zinc-400">{t('transcription.aiInsights.mindmap.processing.description')}</p>
              <div className="mt-4 flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-indigo-300 dark:bg-indigo-600 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="h-2 w-2 rounded-full bg-indigo-300 dark:bg-indigo-600 animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="h-2 w-2 rounded-full bg-indigo-300 dark:bg-indigo-600 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}

          {mindmapStatus === 'idle' && (
            <div className="flex h-full w-full flex-col items-center justify-center px-6 text-center">
              <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
                <svg className="h-8 w-8 text-slate-400 dark:text-zinc-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3" />
                  <circle cx="5" cy="6" r="2" />
                  <circle cx="19" cy="6" r="2" />
                  <circle cx="5" cy="18" r="2" />
                  <circle cx="19" cy="18" r="2" />
                  <line x1="9.5" y1="10" x2="6.5" y2="7.5" />
                  <line x1="14.5" y1="10" x2="17.5" y2="7.5" />
                  <line x1="9.5" y1="14" x2="6.5" y2="16.5" />
                  <line x1="14.5" y1="14" x2="17.5" y2="16.5" />
                </svg>
              </div>
              <p className="text-sm text-slate-600 dark:text-zinc-400 max-w-[260px] mb-4">
                {t('transcription.aiInsights.mindmap.idleDescription')}
              </p>
              <Button variant="primary" size="sm" onClick={onGenerateMindmap}>
                <Sparkles className="h-4 w-4 me-2" />
                {t('transcription.aiInsights.mindmap.empty.generate')}
              </Button>
            </div>
          )}

          {mindmapStatus === 'failed' && (
            <div className="flex h-full w-full flex-col items-center justify-center px-6 text-center">
              <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
                <AlertTriangle className="h-8 w-8 text-slate-400 dark:text-zinc-500" />
              </div>
              <p className="text-sm font-medium text-slate-900 dark:text-white mb-1">{t('transcription.aiInsights.mindmap.failed.title')}</p>
              <p className="text-xs text-slate-500 dark:text-zinc-400 max-w-[280px] mb-4">
                {mindmapError || t('transcription.aiInsights.mindmap.failed.fallbackError')}
              </p>
              <Button variant="primary" size="sm" onClick={onGenerateMindmap}>
                <RefreshCw className="h-4 w-4 me-2" />
                {t('transcription.aiInsights.mindmap.failed.retry')}
              </Button>
            </div>
          )}

          {mindmapStatus === 'completed' && mindmapMarkdown && (
            <>
              {/* Combined Toolbar */}
              <div className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-white dark:bg-zinc-800 rounded-lg shadow-lg border border-slate-200 dark:border-zinc-700 p-1">
                {/* Regenerate button */}
                <ToolbarButton onClick={onGenerateMindmap} title={t('transcription.aiInsights.mindmap.toolbar.regenerate')}>
                  <RefreshCw className="h-4 w-4" />
                </ToolbarButton>

                <div className="w-px h-5 bg-slate-200 dark:bg-zinc-700" />

                {/* Export dropdown */}
                <div className="relative" ref={exportMenuRef}>
                  <ToolbarButton onClick={() => setShowExportMenu(!showExportMenu)} title={t('transcription.aiInsights.mindmap.toolbar.export')}>
                    <Download className="h-4 w-4" />
                  </ToolbarButton>
                  {showExportMenu && (
                    <div className="absolute top-full right-0 mt-2 bg-white dark:bg-zinc-800 rounded-lg shadow-lg border border-slate-200 dark:border-zinc-700 py-1 min-w-[160px] dropdown-animate">
                      <button
                        onClick={handleExportPNG}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 dark:text-zinc-400 hover:bg-slate-50 dark:hover:bg-zinc-700 transition-colors"
                      >
                        <Image className="h-4 w-4" />
                        {t('transcription.aiInsights.mindmap.toolbar.exportPng')}
                      </button>
                      <button
                        onClick={handleExportMarkdown}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 dark:text-zinc-400 hover:bg-slate-50 dark:hover:bg-zinc-700 transition-colors"
                      >
                        <FileText className="h-4 w-4" />
                        {t('transcription.aiInsights.mindmap.toolbar.exportMarkdown')}
                      </button>
                      <button
                        onClick={handleExportXmind}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 dark:text-zinc-400 hover:bg-slate-50 dark:hover:bg-zinc-700 transition-colors"
                      >
                        <FileBox className="h-4 w-4" />
                        {t('transcription.aiInsights.mindmap.toolbar.exportXmind')}
                      </button>
                    </div>
                  )}
                </div>

                <div className="w-px h-5 bg-slate-200 dark:bg-zinc-700" />

                {/* Zoom controls */}
                <ToolbarButton onClick={() => mindmapRef.current?.zoomOut()} title={t('transcription.aiInsights.mindmap.toolbar.zoomOut')}>
                  <ZoomOut className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton onClick={() => mindmapRef.current?.zoomIn()} title={t('transcription.aiInsights.mindmap.toolbar.zoomIn')}>
                  <ZoomIn className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton onClick={() => mindmapRef.current?.resetSize()} title={t('transcription.aiInsights.mindmap.toolbar.resetSize')}>
                  <Minimize2 className="h-4 w-4" />
                </ToolbarButton>

                <div className="w-px h-5 bg-slate-200 dark:bg-zinc-700" />

                {/* Fullscreen toggle */}
                  <ToolbarButton
                    onClick={() => mindmapRef.current?.toggleFullscreen()}
                    title={t('transcription.aiInsights.mindmap.toolbar.fullscreen')}
                  >
                  <Maximize2 className="h-4 w-4" />
                </ToolbarButton>
              </div>
              {mindmapViewer}
            </>
          )}

          {mindmapStatus === 'completed' && !mindmapMarkdown && (
            <div className="flex h-full w-full flex-col items-center justify-center px-6 text-center">
              <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
                <AlertTriangle className="h-8 w-8 text-slate-400 dark:text-zinc-500" />
              </div>
              <p className="text-sm font-medium text-slate-900 dark:text-white mb-1">{t('transcription.aiInsights.mindmap.empty.title')}</p>
              <p className="text-xs text-slate-500 dark:text-zinc-400 max-w-[280px] mb-4">{t('transcription.aiInsights.mindmap.empty.description')}</p>
              <Button variant="primary" size="sm" onClick={onGenerateMindmap}>
                <RefreshCw className="h-4 w-4 me-2" />
                {t('transcription.aiInsights.mindmap.empty.generate')}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Delete Template Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deleteConfirmTemplate}
        onClose={() => setDeleteConfirmTemplate(null)}
        onConfirm={handleDeleteTemplate}
        title={t('transcription.aiInsights.summary.template.deleteTitle')}
        message={t('transcription.aiInsights.summary.template.deleteMessage', { name: deleteConfirmTemplate?.name || '' })}
        confirmText={t('transcription.aiInsights.summary.template.deleteConfirm')}
        variant="danger"
        isLoading={templateSaving}
      />
    </div>
  );
};

// PanelResizer Component - Draggable divider between panels
interface PanelResizerProps {
  onResize: (delta: number) => void;
}

const PanelResizer: React.FC<PanelResizerProps> = ({ onResize }) => {
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    startXRef.current = e.clientX;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startXRef.current;
      startXRef.current = moveEvent.clientX;
      onResize(delta);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  return (
    <div
      className={`hidden lg:flex w-2 flex-shrink-0 items-center justify-center cursor-col-resize group ${
        isDragging ? 'bg-indigo-100 dark:bg-indigo-900/30' : 'hover:bg-slate-100 dark:hover:bg-zinc-800'
      } transition-colors rounded`}
      onMouseDown={handleMouseDown}
    >
      <div
        className={`w-1 h-12 rounded-full transition-colors ${
          isDragging ? 'bg-indigo-400 dark:bg-indigo-500' : 'bg-slate-300 dark:bg-zinc-600 group-hover:bg-slate-400 dark:group-hover:bg-zinc-500'
        }`}
      />
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

// Toast notification component
interface ToastProps {
  message: string;
  isVisible: boolean;
}

const Toast: React.FC<ToastProps> = ({ message, isVisible: propIsVisible }) => {
  const { shouldMount, isVisible } = useTransition(propIsVisible, 300);

  if (!shouldMount) return null;

  return (
    <div className={`fixed bottom-24 left-1/2 -translate-x-1/2 z-50 ${isVisible ? 'toast-in' : 'toast-out'}`}>
      <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-lg shadow-lg">
        <Check className="h-4 w-4 text-green-400" />
        <span className="text-sm font-medium">{message}</span>
      </div>
    </div>
  );
};


const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'zh-CN', label: 'Chinese (Simplified)' },
  { value: 'zh-TW', label: 'Chinese (Traditional)' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'ru', label: 'Russian' },
  { value: 'ar', label: 'Arabic' },
  { value: 'hi', label: 'Hindi' },
  { value: 'th', label: 'Thai' },
  { value: 'vi', label: 'Vietnamese' },
  { value: 'id', label: 'Indonesian' },
  { value: 'ms', label: 'Malay' },
  { value: 'tr', label: 'Turkish' },
  { value: 'pl', label: 'Polish' },
  { value: 'nl', label: 'Dutch' },
  { value: 'sv', label: 'Swedish' },
  { value: 'da', label: 'Danish' },
  { value: 'fi', label: 'Finnish' },
  { value: 'no', label: 'Norwegian' },
  { value: 'cs', label: 'Czech' },
  { value: 'hu', label: 'Hungarian' },
  { value: 'el', label: 'Greek' },
  { value: 'he', label: 'Hebrew' },
  { value: 'uk', label: 'Ukrainian' },
  { value: 'ro', label: 'Romanian' },
  { value: 'bg', label: 'Bulgarian' },
];

const SEGMENT_OVERSCAN = 8;
const SEGMENT_ESTIMATED_HEIGHT_PX = 96;

type SegmentScrollBehavior = 'auto' | 'smooth';

const TranscriptionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getTranscription, getTranscriptionResult, translateTranscription } = useTranscriptions();
  const { data: subscription, loading: subscriptionLoading } = useSubscription();

  const [transcription, setTranscription] = useState<transcriptionsApi.TranscriptionResponse | null>(null);
  const [displayMode, setDisplayMode] = useState<TranslationDisplayMode>('bilingual');
  const [targetLanguage, setTargetLanguage] = useState('en');
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationError, setTranslationError] = useState<string | null>(null);
  const [isTranslationOpen, setIsTranslationOpen] = useState(false);
  const [segments, setSegments] = useState<transcriptionsApi.TranscriptionSegment[]>([]);
  const [speakers, setSpeakers] = useState<transcriptionsApi.TranscriptionSpeaker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [activeSegment, setActiveSegment] = useState<number | null>(null);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isManageSpeakersOpen, setIsManageSpeakersOpen] = useState(false);
  const [aiTab, setAiTab] = useState<'overview' | 'keypoints' | 'mindmap'>('overview');
  const [showRating, setShowRating] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [scrollRequestId, setScrollRequestId] = useState(0);
  const [leftPanelWidth, setLeftPanelWidth] = useState(60); // Percentage width of left panel
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [isRenamingDisplayName, setIsRenamingDisplayName] = useState(false);

  // Audio playback state
  const audioRef = useRef<HTMLAudioElement>(null);
  const segmentsListRef = useRef<HTMLDivElement>(null);
  const lastAutoScrolledSegmentRef = useRef<number | null>(null);
  const lastSegmentIndexRef = useRef<number | null>(null);
  const pendingSeekScrollTimeoutRef = useRef<number | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [volume, setVolume] = useState(1);
  const [editingSegmentId, setEditingSegmentId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [showUpgradeBanner, setShowUpgradeBanner] = useState(true);

  // Speaker edit state
  const [speakerEditSegmentId, setSpeakerEditSegmentId] = useState<number | null>(null);
  const [isSavingSpeaker, setIsSavingSpeaker] = useState(false);

  // Translation state - availableTranslations tracks translation status per language
  const [availableTranslations, setAvailableTranslations] = useState<transcriptionsApi.TranslationItem[]>([]);
  const [, setCurrentTranslation] = useState<transcriptionsApi.TranslationFile | null>(null);
  const [isTranslationStale, setIsTranslationStale] = useState(false);
  const [translationProgress, setTranslationProgress] = useState(0);
  const translationPollingRef = useRef<NodeJS.Timeout | null>(null);
  const translationUpdatesCursorRef = useRef(0);
  const segmentIndexByIdRef = useRef<Map<number, number>>(new Map());

  // Mind map state
  const [mindmapStatus, setMindmapStatus] = useState<MindmapUIStatus>('idle');
  const [mindmapMarkdown, setMindmapMarkdown] = useState<string | null>(null);
  const [mindmapError, setMindmapError] = useState<string | null>(null);
  const mindmapPollingRef = useRef<NodeJS.Timeout | null>(null);

  // Summary state
  const [summaryTemplates, setSummaryTemplates] = useState<transcriptionsApi.SummaryTemplate[]>([]);
  const [selectedSummaryTemplateId, setSelectedSummaryTemplateId] = useState<string>('');
  const [summaryStatus, setSummaryStatus] = useState<SummaryUIStatus>('idle');
  const [summaryMarkdown, setSummaryMarkdown] = useState<string | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const summaryPollingRef = useRef<NodeJS.Timeout | null>(null);

  // Keypoints state
  const [keypointsStatus, setKeypointsStatus] = useState<KeypointsUIStatus>('idle');
  const [keypointsItems, setKeypointsItems] = useState<transcriptionsApi.KeypointsItem[]>([]);
  const [keypointsError, setKeypointsError] = useState<string | null>(null);
  const keypointsPollingRef = useRef<NodeJS.Timeout | null>(null);

  // Newly completed indicators (for showing green dot on tabs)
  const [summaryNewlyCompleted, setSummaryNewlyCompleted] = useState(false);
  const [keypointsNewlyCompleted, setKeypointsNewlyCompleted] = useState(false);
  const [mindmapNewlyCompleted, setMindmapNewlyCompleted] = useState(false);
  // Track previous status to detect transitions to 'completed'
  const prevSummaryStatusRef = useRef<SummaryUIStatus>('idle');
  const prevKeypointsStatusRef = useRef<KeypointsUIStatus>('idle');
  const prevMindmapStatusRef = useRef<MindmapUIStatus>('idle');

  const handleBack = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  }, [navigate]);

  const hasTextEdits = useMemo(() => {
    return segments.some(s => Boolean(s.is_modified));
  }, [segments]);

  // Show toast notification
  const showToastNotification = useCallback((message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  }, []);

  // Panel resize handler
  const containerRef = useRef<HTMLDivElement>(null);
  const handlePanelResize = useCallback((delta: number) => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.offsetWidth;
    const deltaPercent = (delta / containerWidth) * 100;
    setLeftPanelWidth(prev => Math.min(80, Math.max(30, prev + deltaPercent)));
  }, []);

  // Track if we've already loaded the result to prevent repeated fetches
  const resultLoadedRef = useRef(false);
  const displayModeInitializedRef = useRef(false);
  const userChangedDisplayModeRef = useRef(false);

  // Polling for status updates
  const { startPolling, stopPolling } = useTranscriptionPolling(
    id || null,
    useCallback((newStatus: transcriptionsApi.TranscriptionStatusResponse) => {
      // Update transcription status when polling returns new data
      setTranscription(prev => prev ? {
        ...prev,
        status: newStatus.status,
        progress: newStatus.progress,
        processing_time_seconds: newStatus.processing_time_seconds,
        started_at: newStatus.started_at || prev.started_at
      } : prev);

      // If completed, fetch the result (only if not already loaded)
      if (newStatus.status === 'completed' && id && !resultLoadedRef.current) {
        resultLoadedRef.current = true;
        
        // Fetch fresh details to ensure we have file_id for audio playback
        getTranscription(id).then(fullData => {
          setTranscription(fullData);
          if (fullData.file_id && fullData.file_status !== 'transcription_only') {
            fetchAudioUrl(fullData.file_id);
          }
        }).catch(console.error);

        fetchTranscriptionResult(id);
      }
    }, [id, getTranscription])
  );

  // Fetch transcription result
  const fetchTranscriptionResult = async (transcriptionId: string) => {
    try {
      const result = await getTranscriptionResult(transcriptionId);
      const nextSegments = result.segments || [];
      segmentIndexByIdRef.current = new Map(nextSegments.map((segment, index) => [segment.id, index]));
      setSegments(nextSegments);
      setSpeakers(result.speakers || []);
      if (!displayModeInitializedRef.current) {
        const hasTranslation = Boolean(result.segments?.some(segment => segment.translation));
        setDisplayMode(hasTranslation ? 'bilingual' : 'original');
        displayModeInitializedRef.current = true;
      }
      if (result.target_language) {
        setTargetLanguage(result.target_language);
      }
    } catch (err) {
      console.error('Failed to fetch transcription result:', err);
    }
  };

  // Fetch audio URL when transcription is available
  const fetchAudioUrl = async (fileId: string) => {
    try {
      const response = await getDownloadURL(fileId);
      setAudioUrl(response.download_url);
    } catch (err) {
      console.error('Failed to fetch audio URL:', err);
    }
  };

  // Fetch available translations for this transcription
  const fetchAvailableTranslations = useCallback(async (transcriptionId: string) => {
    try {
      const response = await transcriptionsApi.listTranslations(transcriptionId);
      setAvailableTranslations(response.translations || []);
      return response.translations || [];
    } catch (err) {
      console.error('Failed to fetch available translations:', err);
      return [];
    }
  }, []);

  // Fetch translation content and merge with segments
  const fetchTranslationContent = useCallback(async (transcriptionId: string, lang: string) => {
    try {
      const translationFile = await transcriptionsApi.getTranslation(transcriptionId, lang);
      setCurrentTranslation(translationFile);
      setIsTranslationStale(translationFile.is_stale);

      // Merge translation into segments
      if (translationFile.segments && translationFile.segments.length > 0) {
        setSegments(prevSegments => {
          const translationMap = new Map(
            translationFile.segments.map(ts => [ts.id, ts.translation])
          );
          return prevSegments.map(segment => ({
            ...segment,
            translation: translationMap.get(segment.id) || segment.translation
          }));
        });

        if (!userChangedDisplayModeRef.current) {
          setDisplayMode((prev) => (prev === 'original' ? 'bilingual' : prev));
        }
      }

      return translationFile;
    } catch (err) {
      console.error('Failed to fetch translation content:', err);
      return null;
    }
  }, []);

  // Poll for translation status
  const startTranslationPolling = useCallback((transcriptionId: string, lang: string) => {
    // Clear any existing polling
    if (translationPollingRef.current) {
      clearInterval(translationPollingRef.current);
    }

    translationUpdatesCursorRef.current = 0;

    const pollUpdates = async () => {
      try {
        const updates = await transcriptionsApi.getTranslationUpdates(
          transcriptionId,
          lang,
          translationUpdatesCursorRef.current,
          200
        );

        // Update progress
        if (updates.progress !== undefined) {
          setTranslationProgress(updates.progress);
        }

        if (updates.translations && updates.translations.length > 0) {
          setSegments((prevSegments) =>
            applyTranslationUpdates(prevSegments, updates.translations, segmentIndexByIdRef.current)
          );

          if (!userChangedDisplayModeRef.current) {
            setDisplayMode((prev) => (prev === 'original' ? 'bilingual' : prev));
          }
        }

        translationUpdatesCursorRef.current = updates.next_cursor ?? translationUpdatesCursorRef.current;

        if (updates.status === 'completed') {
          // Stop polling and fetch the translation content
          if (translationPollingRef.current) {
            clearInterval(translationPollingRef.current);
            translationPollingRef.current = null;
          }
          await fetchTranslationContent(transcriptionId, lang);
          await fetchAvailableTranslations(transcriptionId);
          setIsTranslating(false);
          setTranslationProgress(100);
          showToastNotification('Translation completed');
        } else if (updates.status === 'failed') {
          // Stop polling on failure
          if (translationPollingRef.current) {
            clearInterval(translationPollingRef.current);
            translationPollingRef.current = null;
          }
          setTranslationError('Translation failed. Please try again.');
          setIsTranslating(false);
          setTranslationProgress(0);
          showToastNotification('Translation failed');
        }
        // Continue polling if still processing
      } catch (err) {
        console.error('Failed to poll translation updates:', err);
      }
    };

    // Poll every 2 seconds
    translationPollingRef.current = setInterval(pollUpdates, 2000);
    // Also poll immediately
    pollUpdates();
  }, [fetchTranslationContent, fetchAvailableTranslations, showToastNotification]);

  // Cleanup translation polling on unmount
  useEffect(() => {
    return () => {
      if (translationPollingRef.current) {
        clearInterval(translationPollingRef.current);
      }
    };
  }, []);

  const stopMindmapPolling = useCallback(() => {
    if (mindmapPollingRef.current) {
      clearInterval(mindmapPollingRef.current);
      mindmapPollingRef.current = null;
    }
  }, []);

  const stopSummaryPolling = useCallback(() => {
    if (summaryPollingRef.current) {
      clearInterval(summaryPollingRef.current);
      summaryPollingRef.current = null;
    }
  }, []);

  const applyMindmapResponse = useCallback((resp: transcriptionsApi.MindmapResponse) => {
    setMindmapStatus(resp.status);
    setMindmapError(resp.error || null);

    if (resp.status === 'completed') {
      setMindmapMarkdown(resp.outline_markdown || '');
      return;
    }

    setMindmapMarkdown(null);
  }, []);

  const applySummaryResponse = useCallback((resp: transcriptionsApi.SummaryResponse) => {
    setSummaryStatus(resp.status);
    setSummaryError(resp.error || null);

    if (resp.status === 'completed') {
      setSummaryMarkdown(resp.summary_markdown || '');
      return;
    }

    setSummaryMarkdown(null);
  }, []);

  const loadSummaryTemplates = useCallback(async () => {
    const resp = await transcriptionsApi.listSummaryTemplates();
    const templates = resp.templates || [];
    setSummaryTemplates(templates);

    if (!selectedSummaryTemplateId && templates.length > 0) {
      setSelectedSummaryTemplateId(templates[0].id);
    }

    if (selectedSummaryTemplateId && !templates.some(t => t.id === selectedSummaryTemplateId) && templates.length > 0) {
      setSelectedSummaryTemplateId(templates[0].id);
    }
  }, [selectedSummaryTemplateId]);

  const createSummaryTemplate = useCallback(async (name: string, prompt: string) => {
    const tpl = await transcriptionsApi.createSummaryTemplate({ name, prompt });
    await loadSummaryTemplates();
    setSelectedSummaryTemplateId(tpl.id);
  }, [loadSummaryTemplates]);

  const updateSummaryTemplate = useCallback(async (templateId: string, name: string, prompt: string) => {
    await transcriptionsApi.updateSummaryTemplate(templateId, { name, prompt });
    await loadSummaryTemplates();
  }, [loadSummaryTemplates]);

  const deleteSummaryTemplate = useCallback(async (templateId: string) => {
    await transcriptionsApi.deleteSummaryTemplate(templateId);
    await loadSummaryTemplates();
    // If the deleted template was selected, select the first available
    if (selectedSummaryTemplateId === templateId) {
      const remaining = summaryTemplates.filter(t => t.id !== templateId);
      if (remaining.length > 0) {
        setSelectedSummaryTemplateId(remaining[0].id);
      }
    }
  }, [loadSummaryTemplates, selectedSummaryTemplateId, summaryTemplates]);

  const fetchSummary = useCallback(async (transcriptionId: string, templateId: string, isInitialLoad = false) => {
    try {
      const resp = await transcriptionsApi.getSummary(transcriptionId, templateId);
      // On initial load, treat historical 'failed' status as 'idle' to allow fresh generation
      if (isInitialLoad && resp.status === 'failed') {
        setSummaryStatus('idle');
        setSummaryMarkdown(null);
        setSummaryError(null);
        return null;
      }
      applySummaryResponse(resp);
      return resp;
    } catch {
      // Any error when fetching existing summary means no summary exists yet.
      // Show idle state so user can generate one. Only requestSummary failures
      // should show the failed state.
      setSummaryStatus('idle');
      setSummaryMarkdown(null);
      setSummaryError(null);
      return null;
    }
  }, [applySummaryResponse]);

  const startSummaryPolling = useCallback((transcriptionId: string, templateId: string) => {
    stopSummaryPolling();

    const pollStatus = async () => {
      const resp = await fetchSummary(transcriptionId, templateId);
      if (!resp) {
        stopSummaryPolling();
        return;
      }

      if (resp.status === 'completed' || resp.status === 'failed') {
        stopSummaryPolling();
      }
    };

    summaryPollingRef.current = setInterval(pollStatus, 2000);
    pollStatus();
  }, [fetchSummary, stopSummaryPolling]);

  const requestSummary = useCallback(async () => {
    if (!id) return;
    if (!selectedSummaryTemplateId) {
      showToastNotification('Please select a summary template');
      return;
    }

    setSummaryError(null);
    setSummaryStatus('pending');
    setSummaryMarkdown(null);

    try {
      const resp = await transcriptionsApi.requestSummary(id, selectedSummaryTemplateId);
      applySummaryResponse(resp);

      if (resp.status === 'pending' || resp.status === 'processing') {
        startSummaryPolling(id, selectedSummaryTemplateId);
      }
    } catch (err) {
      setSummaryStatus('failed');
      setSummaryMarkdown(null);
      setSummaryError(handleApiError(err));
    }
  }, [applySummaryResponse, id, selectedSummaryTemplateId, showToastNotification, startSummaryPolling]);

  const stopKeypointsPolling = useCallback(() => {
    if (keypointsPollingRef.current) {
      clearInterval(keypointsPollingRef.current);
      keypointsPollingRef.current = null;
    }
  }, []);

  const applyKeypointsResponse = useCallback((resp: transcriptionsApi.KeypointsResponse) => {
    setKeypointsStatus(resp.status);
    setKeypointsItems(resp.items || []);
    setKeypointsError(resp.error || null);
  }, []);

  const fetchKeypoints = useCallback(async (transcriptionId: string, isInitialLoad = false) => {
    try {
      const resp = await transcriptionsApi.getKeypoints(transcriptionId);
      // On initial load, treat historical 'failed' status as 'idle' to allow fresh generation
      if (isInitialLoad && resp.status === 'failed') {
        setKeypointsStatus('idle');
        setKeypointsItems([]);
        setKeypointsError(null);
        return null;
      }
      applyKeypointsResponse(resp);
      return resp;
    } catch {
      // Any error when fetching existing keypoints means none exist yet.
      // Show idle state so user can generate them.
      setKeypointsStatus('idle');
      setKeypointsItems([]);
      setKeypointsError(null);
      return null;
    }
  }, [applyKeypointsResponse]);

  const startKeypointsPolling = useCallback((transcriptionId: string) => {
    stopKeypointsPolling();

    const pollStatus = async () => {
      const resp = await fetchKeypoints(transcriptionId);
      if (!resp) {
        stopKeypointsPolling();
        return;
      }

      if (resp.status === 'completed' || resp.status === 'failed') {
        stopKeypointsPolling();
      }
    };

    keypointsPollingRef.current = setInterval(pollStatus, 2000);
    pollStatus();
  }, [fetchKeypoints, stopKeypointsPolling]);

  const requestKeypoints = useCallback(async () => {
    if (!id) return;

    setKeypointsError(null);
    setKeypointsStatus('pending');
    setKeypointsItems([]);

    try {
      const resp = await transcriptionsApi.requestKeypoints(id);
      applyKeypointsResponse(resp);

      if (resp.status === 'pending' || resp.status === 'processing') {
        startKeypointsPolling(id);
      }
    } catch (err) {
      setKeypointsStatus('failed');
      setKeypointsItems([]);
      setKeypointsError(handleApiError(err));
    }
  }, [applyKeypointsResponse, id, startKeypointsPolling]);

  const fetchMindmap = useCallback(async (transcriptionId: string, isInitialLoad = false) => {
    try {
      const resp = await transcriptionsApi.getMindmap(transcriptionId);
      // On initial load, treat historical 'failed' status as 'idle' to allow fresh generation
      if (isInitialLoad && resp.status === 'failed') {
        setMindmapStatus('idle');
        setMindmapMarkdown(null);
        setMindmapError(null);
        return null;
      }
      applyMindmapResponse(resp);
      return resp;
    } catch {
      // Any error when fetching existing mindmap means none exists yet.
      // Show idle state so user can generate one.
      setMindmapStatus('idle');
      setMindmapMarkdown(null);
      setMindmapError(null);
      return null;
    }
  }, [applyMindmapResponse]);

  const startMindmapPolling = useCallback((transcriptionId: string) => {
    stopMindmapPolling();

    const pollStatus = async () => {
      const resp = await fetchMindmap(transcriptionId);
      if (!resp) {
        stopMindmapPolling();
        return;
      }

      if (resp.status === 'completed' || resp.status === 'failed') {
        stopMindmapPolling();
      }
    };

    mindmapPollingRef.current = setInterval(pollStatus, 2000);
    pollStatus();
  }, [fetchMindmap, stopMindmapPolling]);

  const requestMindmap = useCallback(async () => {
    if (!id) return;

    setMindmapError(null);
    setMindmapStatus('pending');
    setMindmapMarkdown(null);

    try {
      const resp = await transcriptionsApi.requestMindmap(id);
      applyMindmapResponse(resp);

      if (resp.status === 'pending' || resp.status === 'processing') {
        startMindmapPolling(id);
      }
    } catch (err) {
      setMindmapStatus('failed');
      setMindmapMarkdown(null);
      setMindmapError(handleApiError(err));
    }
  }, [applyMindmapResponse, id, startMindmapPolling]);

  // Load summary templates once.
  useEffect(() => {
    loadSummaryTemplates().catch((err) => {
      console.error('Failed to load summary templates:', err);
    });
  }, [loadSummaryTemplates]);

  // Load AI artifact state via ai-status to avoid 404 noise when not generated yet.
  useEffect(() => {
    if (!id || loading || !selectedSummaryTemplateId) return;
    if (transcription?.status !== 'completed') return;

    const load = async () => {
      try {
        const aiStatus = await transcriptionsApi.getTranscriptionAIStatus(id, selectedSummaryTemplateId);

        if (aiStatus.summary.status === 'not_started') {
          setSummaryStatus('idle');
          setSummaryMarkdown(null);
          setSummaryError(null);
          stopSummaryPolling();
        } else {
          const resp = await fetchSummary(id, selectedSummaryTemplateId, true);
          if (resp && (resp.status === 'pending' || resp.status === 'processing')) {
            startSummaryPolling(id, selectedSummaryTemplateId);
          }
        }

        if (aiStatus.keypoints.status === 'not_started') {
          setKeypointsStatus('idle');
          setKeypointsItems([]);
          setKeypointsError(null);
          stopKeypointsPolling();
        } else {
          const resp = await fetchKeypoints(id, true);
          if (resp && (resp.status === 'pending' || resp.status === 'processing')) {
            startKeypointsPolling(id);
          }
        }

        if (aiStatus.mindmap.status === 'not_started') {
          setMindmapStatus('idle');
          setMindmapMarkdown(null);
          setMindmapError(null);
          stopMindmapPolling();
        } else {
          const resp = await fetchMindmap(id, true);
          if (resp && (resp.status === 'pending' || resp.status === 'processing')) {
            startMindmapPolling(id);
          }
        }
      } catch (err) {
        console.error('Failed to load AI status:', err);
      }
    };

    void load();
  }, [
    fetchKeypoints,
    fetchMindmap,
    fetchSummary,
    id,
    loading,
    selectedSummaryTemplateId,
    startKeypointsPolling,
    startMindmapPolling,
    startSummaryPolling,
    stopKeypointsPolling,
    stopMindmapPolling,
    stopSummaryPolling,
    transcription?.status,
  ]);

  // Cleanup polling on unmount.
  useEffect(() => stopSummaryPolling, [stopSummaryPolling]);
  useEffect(() => stopKeypointsPolling, [stopKeypointsPolling]);
  useEffect(() => stopMindmapPolling, [stopMindmapPolling]);

  // Detect status transitions to 'completed' and show green dot indicator
  useEffect(() => {
    const wasProcessing = prevSummaryStatusRef.current === 'pending' || prevSummaryStatusRef.current === 'processing';
    if (wasProcessing && summaryStatus === 'completed' && aiTab !== 'overview') {
      setSummaryNewlyCompleted(true);
    }
    prevSummaryStatusRef.current = summaryStatus;
  }, [summaryStatus, aiTab]);

  useEffect(() => {
    const wasProcessing = prevKeypointsStatusRef.current === 'pending' || prevKeypointsStatusRef.current === 'processing';
    if (wasProcessing && keypointsStatus === 'completed' && aiTab !== 'keypoints') {
      setKeypointsNewlyCompleted(true);
    }
    prevKeypointsStatusRef.current = keypointsStatus;
  }, [keypointsStatus, aiTab]);

  useEffect(() => {
    const wasProcessing = prevMindmapStatusRef.current === 'pending' || prevMindmapStatusRef.current === 'processing';
    if (wasProcessing && mindmapStatus === 'completed' && aiTab !== 'mindmap') {
      setMindmapNewlyCompleted(true);
    }
    prevMindmapStatusRef.current = mindmapStatus;
  }, [mindmapStatus, aiTab]);

  // Clear newly completed indicator when user switches to that tab
  useEffect(() => {
    if (aiTab === 'overview') {
      setSummaryNewlyCompleted(false);
    } else if (aiTab === 'keypoints') {
      setKeypointsNewlyCompleted(false);
    } else if (aiTab === 'mindmap') {
      setMindmapNewlyCompleted(false);
    }
  }, [aiTab]);

  // Load translation content when target language changes
  useEffect(() => {
    if (!id || loading) return;

    const translationItem = availableTranslations.find(t => t.language === targetLanguage);
    const hasCompletedTranslationFile = translationItem?.status === 'completed';

    if (!hasCompletedTranslationFile) {
      if (translationPollingRef.current) {
        clearInterval(translationPollingRef.current);
        translationPollingRef.current = null;
      }
      setIsTranslating(false);
      setTranslationProgress(0);
      setSegments(prevSegments =>
        prevSegments.map(segment => ({
          ...segment,
          translation: undefined
        }))
      );
      setIsTranslationStale(false);
      return;
    }

    void fetchTranslationContent(id, targetLanguage);
  }, [targetLanguage, availableTranslations, id, loading, fetchTranslationContent]);

  // Initial fetch
  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setError('No transcription ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const transcriptionData = await getTranscription(id);
        setTranscription(transcriptionData);
        setIsEdited(transcriptionData.is_edited ?? false);

        // If completed, fetch segments and audio URL
        if (transcriptionData.status === 'completed') {
          await fetchTranscriptionResult(id);
          resultLoadedRef.current = true;
          // Fetch audio URL for playback only if original file is available
          if (transcriptionData.file_id && transcriptionData.file_status !== 'transcription_only') {
            fetchAudioUrl(transcriptionData.file_id);
          }
          // Fetch available translations and load the first completed one
          const translations = await fetchAvailableTranslations(id);
          const completedTranslation = translations.find(t => t.status === 'completed');
          const processingTranslation = translations.find(t => t.status === 'processing' || t.status === 'pending');

          // If there's a translation in progress, resume polling
          if (processingTranslation) {
            setTargetLanguage(processingTranslation.language);
            setIsTranslating(true);
            setTranslationProgress(processingTranslation.progress || 0);
            startTranslationPolling(id, processingTranslation.language);
          } else if (completedTranslation) {
            setTargetLanguage(completedTranslation.language);
            await fetchTranslationContent(id, completedTranslation.language);
          }
        } else if (['pending', 'queued', 'processing'].includes(transcriptionData.status)) {
          // Start polling if still processing
          startPolling(3000);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load transcription');
      } finally {
        setLoading(false);
      }
    };

    // Reset state when id changes
    resultLoadedRef.current = false;
    displayModeInitializedRef.current = false;
    userChangedDisplayModeRef.current = false;
    translationUpdatesCursorRef.current = 0;
    segmentIndexByIdRef.current = new Map();
    setDisplayMode('bilingual');
    setTargetLanguage('en');
    setTranslationError(null);
    setIsTranslationOpen(false);
    setEditingSegmentId(null);
    setEditingText('');
    setIsEdited(false);
    setAvailableTranslations([]);
    setCurrentTranslation(null);
    setIsTranslationStale(false);
    if (translationPollingRef.current) {
      clearInterval(translationPollingRef.current);
      translationPollingRef.current = null;
    }
    fetchData();
    window.scrollTo(0, 0);
    segmentsListRef.current?.scrollTo({ top: 0, behavior: 'auto' });

    return () => {
      stopPolling();
    };
  }, [id]);

  // Audio event handlers
  const handleAudioTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  }, []);

  const handleAudioLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  }, []);

  const handleAudioPlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handleAudioPause = useCallback(() => {
    setIsPlaying(false);
    // Allow the next play/resume to re-center the active segment.
    lastAutoScrolledSegmentRef.current = null;
  }, []);

  const handleAudioEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  const handleAudioError = useCallback((e: React.SyntheticEvent<HTMLAudioElement>) => {
    const audio = e.currentTarget;
    const error = audio.error;
    console.error('Audio playback error:', {
      code: error?.code,
      message: error?.message,
      src: audio.src,
      networkState: audio.networkState,
      readyState: audio.readyState,
    });
  }, []);

  useEffect(() => {
    return () => {
      if (pendingSeekScrollTimeoutRef.current != null) {
        clearTimeout(pendingSeekScrollTimeoutRef.current);
        pendingSeekScrollTimeoutRef.current = null;
      }
    };
  }, []);

  const formatSegmentTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const segmentIndexById = useMemo(() => {
    const map = new Map<number, number>();
    for (let idx = 0; idx < segments.length; idx += 1) {
      map.set(segments[idx].id, idx);
    }
    return map;
  }, [segments]);

  const estimateSegmentSize = useCallback(() => SEGMENT_ESTIMATED_HEIGHT_PX, []);

  const segmentsVirtualizer = useVirtualizer({
    count: segments.length,
    getScrollElement: () => segmentsListRef.current,
    estimateSize: estimateSegmentSize,
    overscan: SEGMENT_OVERSCAN,
  });

  const scrollSegmentIntoView = useCallback((segmentId: number, behavior: SegmentScrollBehavior = 'smooth') => {
    const idx = segmentIndexById.get(segmentId);
    if (idx == null) return;
    segmentsVirtualizer.scrollToIndex(idx, { align: 'center', behavior });

    // Virtualized lists with variable row heights can accumulate estimation error
    // across long jumps. Retry once after layout to ensure the target row renders.
    requestAnimationFrame(() => {
      const isRendered = segmentsVirtualizer.getVirtualItems().some(item => item.index === idx);
      if (!isRendered) {
        segmentsVirtualizer.scrollToIndex(idx, { align: 'center', behavior: 'auto' });
      }
    });
  }, [segmentIndexById, segmentsVirtualizer]);

  const findSegmentIndexForTime = useCallback((time: number): number | null => {
    const count = segments.length;
    if (count === 0) return null;

    const clampIndex = (idx: number) => Math.min(count - 1, Math.max(0, idx));
    const isInSegment = (idx: number) => time >= segments[idx].start && time < segments[idx].end;

    const hint = lastSegmentIndexRef.current;
    if (hint != null && hint >= 0 && hint < count) {
      let idx = hint;
      if (isInSegment(idx)) return idx;

      if (time >= segments[idx].end) {
        while (idx + 1 < count && time >= segments[idx].end) idx += 1;
        idx = clampIndex(idx);
        if (isInSegment(idx)) return idx;
      } else if (time < segments[idx].start) {
        while (idx - 1 >= 0 && time < segments[idx].start) idx -= 1;
        idx = clampIndex(idx);
        if (isInSegment(idx)) return idx;
      }
    }

    // Binary search fallback for large jumps
    let lo = 0;
    let hi = count - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const seg = segments[mid];
      if (time < seg.start) {
        hi = mid - 1;
      } else if (time >= seg.end) {
        lo = mid + 1;
      } else {
        return mid;
      }
    }

    return null;
  }, [segments]);

  const handleSeek = useCallback((time: number) => {
    // Guard against non-finite values (Infinity, NaN)
    if (!Number.isFinite(time) || time < 0) {
      console.warn('Invalid seek time:', time);
      return;
    }

    setCurrentTime(time);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }

    // Find and highlight the segment at the seek position
    const targetIndex = findSegmentIndexForTime(time);
    if (targetIndex == null) return;

    const targetSegment = segments[targetIndex];
    lastSegmentIndexRef.current = targetIndex;
    setActiveSegment(targetSegment.id);

    if (pendingSeekScrollTimeoutRef.current != null) {
      clearTimeout(pendingSeekScrollTimeoutRef.current);
    }
    pendingSeekScrollTimeoutRef.current = window.setTimeout(() => {
      scrollSegmentIntoView(targetSegment.id, 'smooth');
      pendingSeekScrollTimeoutRef.current = null;
    }, 50);

    // Prevent autoplay effect from immediately re-scrolling the same segment.
    lastAutoScrolledSegmentRef.current = targetSegment.id;
  }, [findSegmentIndexForTime, scrollSegmentIntoView, segments]);

  const handleSkipBack = () => {
    const newTime = Math.max(0, currentTime - 15);
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  const handleSkipForward = () => {
    const maxTime = audioDuration || transcription?.duration || 100;
    const newTime = Math.min(maxTime, currentTime + 15);
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(err => {
        console.error('Failed to play audio:', err);
      });
    }
  };

  const handleSpeedChange = (speed: number) => {
    setPlaybackSpeed(speed);
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
  };

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleRenameDisplayName = useCallback(
    async (nextName: string) => {
      if (!transcription) return;
      const previousDisplayName = transcription.display_name;

      setIsRenamingDisplayName(true);
      setTranscription((prev) => prev ? { ...prev, display_name: nextName } : prev);
      try {
        const updated = await transcriptionsApi.updateTranscriptionDisplayName(transcription.id, nextName);
        setTranscription((prev) => prev ? { ...prev, display_name: updated.display_name ?? nextName } : prev);
      } catch (err) {
        setTranscription((prev) => prev ? { ...prev, display_name: previousDisplayName } : prev);
        showToastNotification('Failed to rename transcription');
        throw err;
      } finally {
        setIsRenamingDisplayName(false);
      }
    },
    [showToastNotification, transcription]
  );

  const speakersById = useMemo(() => {
    return new Map(speakers.map(s => [s.id, s.name]));
  }, [speakers]);

  const getSpeakerDisplayName = useCallback((segment: transcriptionsApi.TranscriptionSegment) => {
    const raw = segment.speaker?.trim() || '';
    if (!raw) return 'Speaker';
    const mapped = speakersById.get(raw)?.trim();
    return mapped || raw;
  }, [speakersById]);

  const hasSpeakerInformation = useMemo(() => {
    if (speakers.length > 0) return true;
    for (const seg of segments) {
      if ((seg.speaker ?? '').trim() !== '') {
        return true;
      }
    }
    return false;
  }, [segments, speakers]);

  const speakerGroups = useMemo<SpeakerGroup[]>(() => {
    if (!hasSpeakerInformation) return [];
    const grouped = new Map<string, SpeakerGroup>();
    for (const seg of segments) {
      const displayName = getSpeakerDisplayName(seg).trim() || 'Speaker';
      const existing = grouped.get(displayName);
      if (existing) {
        existing.segmentCount += 1;
        continue;
      }
      grouped.set(displayName, {
        displayName,
        segmentCount: 1,
        segmentId: seg.id,
      });
    }
    return Array.from(grouped.values());
  }, [getSpeakerDisplayName, hasSpeakerInformation, segments]);

  const speakerEditMatchCount = useMemo(() => {
    if (speakerEditSegmentId === null) return 0;
    const editingSegment = segments.find(seg => seg.id === speakerEditSegmentId);
    if (!editingSegment) return 0;

    const target = getSpeakerDisplayName(editingSegment).trim();
    if (!target) return 0;

    let count = 0;
    for (const seg of segments) {
      if (getSpeakerDisplayName(seg).trim() === target) {
        count += 1;
      }
    }
    return count;
  }, [getSpeakerDisplayName, segments, speakerEditSegmentId]);

  const openSpeakerEdit = useCallback((segmentId: number) => {
    setEditingSegmentId(null);
    setEditingText('');
    setSpeakerEditSegmentId(segmentId);
    setActiveSegment(segmentId);
    setIsSavingSpeaker(false);
  }, []);

  const closeSpeakerEdit = useCallback(() => {
    setSpeakerEditSegmentId(null);
    setIsSavingSpeaker(false);
  }, []);

  const handleSaveSegment = useCallback(
    async (segmentId: number, text: string) => {
      if (!id) return;
      const currentSegment = segments.find(segment => segment.id === segmentId);
      if (!currentSegment) {
        showToastNotification('Segment not found. Please refresh.');
        return;
      }

      if (currentSegment.text === text) {
        setEditingSegmentId(null);
        setEditingText('');
        return;
      }

      try {
        setIsSaving(true);
        const response = await transcriptionsApi.updateTranscriptionContent(id, [
          { id: segmentId, text }
        ]);
        setSegments(prev => prev.map(segment => (
          segment.id === segmentId ? { ...segment, text, is_modified: true } : segment
        )));
        setIsEdited(response.is_edited);
        setTranscription(prev => prev ? {
          ...prev,
          is_edited: response.is_edited,
          last_edited_at: response.last_edited_at
        } : prev);
        showToastNotification('Segment updated');
        setEditingSegmentId(null);
        setEditingText('');
      } catch (err) {
        console.error('Failed to save segment edit:', err);
        showToastNotification('Failed to save. Please try again.');
      } finally {
        setIsSaving(false);
      }
    },
    [id, segments, showToastNotification]
  );

  const handleSaveSpeaker = useCallback(async (
    segmentId: number,
    newSpeakerRaw: string,
    scope: 'segment' | 'all_same'
  ) => {
    if (!id) return false;

    const newSpeaker = newSpeakerRaw.trim();
    if (!newSpeaker) {
      showToastNotification('Speaker name is required');
      return false;
    }

    try {
      setIsSavingSpeaker(true);
      const resp = await transcriptionsApi.updateTranscriptionSpeaker(id, {
        segment_id: segmentId,
        new_speaker: newSpeaker,
        scope,
      });

      setIsEdited(resp.is_edited);
      setTranscription(prev => prev ? {
        ...prev,
        is_edited: resp.is_edited,
        last_edited_at: resp.last_edited_at,
        speaker_count: resp.speaker_count,
      } : prev);

      await fetchTranscriptionResult(id);
      showToastNotification('Speaker updated');
      closeSpeakerEdit();
      return true;
    } catch (err) {
      console.error('Failed to save speaker edit:', err);
      showToastNotification('Failed to save. Please try again.');
      return false;
    } finally {
      setIsSavingSpeaker(false);
    }
  }, [closeSpeakerEdit, fetchTranscriptionResult, id, showToastNotification]);

  const handleRenameSpeakerGroup = useCallback(async (segmentId: number, newSpeaker: string) => {
    return handleSaveSpeaker(segmentId, newSpeaker, 'all_same');
  }, [handleSaveSpeaker]);

  const handleRevertTranscription = async () => {
    if (!id) return;
    try {
      setIsReverting(true);
      const response = await transcriptionsApi.revertTranscription(id);
      const result = await getTranscriptionResult(id);
      const nextSegments = result.segments || [];
      segmentIndexByIdRef.current = new Map(nextSegments.map((segment, index) => [segment.id, index]));
      setSegments(nextSegments);
      setSpeakers(result.speakers || []);
      setIsEdited(response.is_edited);
      setTranscription(prev => prev ? {
        ...prev,
        is_edited: response.is_edited,
        last_edited_at: undefined
      } : prev);
      setEditingSegmentId(null);
      setEditingText('');
      showToastNotification(response.message || 'Transcript reverted to original');
    } catch (err) {
      console.error('Failed to revert transcription:', err);
      showToastNotification('Failed to revert. Please try again.');
    } finally {
      setIsReverting(false);
      setShowRevertConfirm(false);
    }
  };

  // Handle retry for failed transcription
  const handleRetryTranscription = async () => {
    if (!id) return;
    try {
      setIsRetrying(true);
      const response = await transcriptionsApi.retryTranscription(id);
      setTranscription(response);
      showToastNotification(t('transcription.aiInsights.toast.retryRequested'));
      // Start polling for status updates
      startPolling(3000);
    } catch (err) {
      console.error('Failed to retry transcription:', err);
      showToastNotification('Failed to retry. Please try again.');
    } finally {
      setIsRetrying(false);
    }
  };

  const handleTranslate = async () => {
    if (!id) return;
    const hadTranslation = segments.some(segment => Boolean(segment.translation));
    setIsTranslating(true);
    setTranslationError(null);
    setTranslationProgress(0);

    try {
      await translateTranscription(id, targetLanguage);
      showToastNotification(hadTranslation ? 'Translation restarted' : 'Translation started');
      // Start polling for translation status
      startTranslationPolling(id, targetLanguage);
    } catch (err) {
      const code = getApiErrorCode(err);
      if (code === 'TRANSLATION_IN_PROGRESS') {
        showToastNotification('Translation already in progress');
        startTranslationPolling(id, targetLanguage);
        return;
      }

      const message = handleApiError(err);
      setTranslationError(message);
      showToastNotification(message);
      setIsTranslating(false);
    }
    // Note: isTranslating will be set to false when polling detects completion/failure
  };

  // Check if the current target language has a completed translation
  const currentLangTranslation = availableTranslations.find(t => t.language === targetLanguage);
  const hasTranslationForCurrentLang = currentLangTranslation?.status === 'completed';
  const isCurrentLangStale = currentLangTranslation?.is_stale ?? isTranslationStale;

  // For display purposes, check if segments have translation content
  const hasTranslationContent = useMemo(() => {
    return segments.some(segment => Boolean(segment.translation && segment.translation.trim()));
  }, [segments]);

  const effectiveDisplayMode = (hasTranslationForCurrentLang || hasTranslationContent) ? displayMode : 'original';

  // Search functionality
  const deferredSearchValue = useDeferredValue(searchValue);
  const searchQuery = searchValue.trim() === '' ? '' : deferredSearchValue;
  const searchMatches = useMemo(() => {
    return findAllMatches(segments, searchQuery, effectiveDisplayMode);
  }, [segments, searchQuery, effectiveDisplayMode]);

  const matchesBySegmentId = useMemo(() => {
    return groupMatchesBySegmentId(searchMatches);
  }, [searchMatches]);

  const currentMatch = searchMatches[currentMatchIndex] ?? null;

  // Reset currentMatchIndex when searchValue changes or matches change
  useEffect(() => {
    setCurrentMatchIndex(0);
  }, [searchValue, effectiveDisplayMode]);

  useEffect(() => {
    if (currentMatchIndex === 0) return;
    if (searchMatches.length === 0 || currentMatchIndex >= searchMatches.length) {
      setCurrentMatchIndex(0);
    }
  }, [currentMatchIndex, searchMatches.length]);

  const requestScrollToCurrentMatch = useCallback(() => {
    setScrollRequestId((prev) => prev + 1);
  }, []);

  const handleNextMatch = useCallback(() => {
    if (searchMatches.length === 0) return;
    setCurrentMatchIndex(prev => (prev + 1) % searchMatches.length);
    requestScrollToCurrentMatch();
  }, [requestScrollToCurrentMatch, searchMatches.length]);

  const handlePrevMatch = useCallback(() => {
    if (searchMatches.length === 0) return;
    setCurrentMatchIndex(prev => (prev - 1 + searchMatches.length) % searchMatches.length);
    requestScrollToCurrentMatch();
  }, [requestScrollToCurrentMatch, searchMatches.length]);

  const handleClearSearch = useCallback(() => {
    setSearchValue('');
    setCurrentMatchIndex(0);
  }, []);

  // Auto-highlight and scroll to current playing segment
  useEffect(() => {
    if (!isPlaying || segments.length === 0) return;

    // Find the segment that contains the current time
    const currentIndex = findSegmentIndexForTime(currentTime);
    if (currentIndex == null) return;
    const currentSegment = segments[currentIndex];
    lastSegmentIndexRef.current = currentIndex;

    if (!currentSegment) return;

    if (currentSegment.id !== activeSegment) {
      setActiveSegment(currentSegment.id);
    }

    // Follow playback: center the current segment in the transcript view.
    // Only scroll when the segment changes (or when playback resumes and we reset the ref).
    if (currentSegment.id !== lastAutoScrolledSegmentRef.current) {
      const behavior: SegmentScrollBehavior = lastAutoScrolledSegmentRef.current == null ? 'auto' : 'smooth';
      lastAutoScrolledSegmentRef.current = currentSegment.id;
      scrollSegmentIntoView(currentSegment.id, behavior);
    }
  }, [currentTime, segments, isPlaying, activeSegment, findSegmentIndexForTime, scrollSegmentIntoView]);

  // Auto-scroll to current match (navigation only)
  useEffect(() => {
    if (scrollRequestId === 0) return;
    if (searchMatches.length === 0 || currentMatchIndex >= searchMatches.length) return;
    const match = searchMatches[currentMatchIndex];
    scrollSegmentIntoView(match.segmentId, 'smooth');
  }, [currentMatchIndex, scrollRequestId, scrollSegmentIntoView, searchMatches]);

  const handleCancelEdit = useCallback(() => {
    setEditingSegmentId(null);
    setEditingText('');
  }, []);

  const { isScrollable, isAtBottom, update: updateScrollBoundary } = useScrollBoundary(segmentsListRef);

  const handleScrollToTop = useCallback(() => {
    const el = segmentsListRef.current;
    if (!el) return;
    
    const start = el.scrollTop;
    if (start === 0) return;
    
    const duration = 300;
    const startTime = performance.now();
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
    
    const step = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      el.scrollTop = start * (1 - easeOutCubic(progress));
      
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        updateScrollBoundary();
      }
    };
    
    requestAnimationFrame(step);
  }, [updateScrollBoundary]);

  const handleScrollToBottom = useCallback(() => {
    if (segments.length === 0) return;
    const lastIndex = segments.length - 1;
    segmentsVirtualizer.scrollToIndex(lastIndex, { align: 'end', behavior: 'smooth' });
    setTimeout(() => {
      segmentsVirtualizer.scrollToIndex(lastIndex, { align: 'end', behavior: 'auto' });
      updateScrollBoundary();
    }, 300);
  }, [segments.length, segmentsVirtualizer, updateScrollBoundary]);

  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      updateScrollBoundary();
    });
    return () => cancelAnimationFrame(rafId);
  }, [segments.length, segmentsVirtualizer.getTotalSize(), updateScrollBoundary]);

  const segmentEventHandlers = useMemo(() => {
    const handlers = new Map<number, { onEdit: () => void; onSave: (text: string) => void; onSelect: () => void; onPlay: () => void }>();
    for (const segment of segments) {
      handlers.set(segment.id, {
        onEdit: () => {
          setEditingSegmentId(segment.id);
          setEditingText(segment.text);
          setActiveSegment(segment.id);
        },
        onSave: (text: string) => {
          void handleSaveSegment(segment.id, text);
        },
        onSelect: () => setActiveSegment(segment.id),
        onPlay: () => {
          handleSeek(segment.start);
          if (audioRef.current) {
            audioRef.current.play().catch(err => {
              console.error('Failed to play audio:', err);
            });
          }
        },
      });
    }
    return handlers;
  }, [handleSaveSegment, handleSeek, segments]);

  const aiInsightsPanel = useMemo(() => {
    return (
      <AIInsightsPanel
        activeTab={aiTab}
        onTabChange={setAiTab}
        summaryTemplates={summaryTemplates}
        selectedSummaryTemplateId={selectedSummaryTemplateId}
        onSelectSummaryTemplateId={setSelectedSummaryTemplateId}
        onCreateSummaryTemplate={createSummaryTemplate}
        onUpdateSummaryTemplate={updateSummaryTemplate}
        onDeleteSummaryTemplate={deleteSummaryTemplate}
        summaryStatus={summaryStatus}
        summaryMarkdown={summaryMarkdown}
        summaryError={summaryError}
        onGenerateSummary={requestSummary}
        keypointsStatus={keypointsStatus}
        keypointsItems={keypointsItems}
        keypointsError={keypointsError}
        onGenerateKeyPoints={requestKeypoints}
        mindmapStatus={mindmapStatus}
        mindmapMarkdown={mindmapMarkdown}
        mindmapError={mindmapError}
        onGenerateMindmap={requestMindmap}
        summaryNewlyCompleted={summaryNewlyCompleted}
        keypointsNewlyCompleted={keypointsNewlyCompleted}
        mindmapNewlyCompleted={mindmapNewlyCompleted}
      />
    );
  }, [
    aiTab,
    createSummaryTemplate,
    deleteSummaryTemplate,
    keypointsError,
    keypointsItems,
    keypointsNewlyCompleted,
    keypointsStatus,
    mindmapError,
    mindmapMarkdown,
    mindmapNewlyCompleted,
    mindmapStatus,
    requestMindmap,
    requestKeypoints,
    requestSummary,
    selectedSummaryTemplateId,
    setAiTab,
    setSelectedSummaryTemplateId,
    summaryError,
    summaryMarkdown,
    summaryNewlyCompleted,
    summaryStatus,
    summaryTemplates,
    updateSummaryTemplate,
  ]);

  const handleExportSubmit = (format: string, options: ExportOptions) => {
    let content = '';
    let mimeType = 'text/plain';
    let extension = format;

    try {
      switch (format) {
        case 'txt':
          content = generateTXT(segments, options);
          mimeType = 'text/plain';
          break;
        case 'json':
          content = generateJSON(segments);
          mimeType = 'application/json';
          break;
        case 'md':
          content = generateMarkdown(segments, options);
          mimeType = 'text/markdown';
          break;
        case 'srt':
          content = generateSRT(segments);
          mimeType = 'text/plain';
          break;
        case 'vtt':
          content = generateVTT(segments);
          mimeType = 'text/vtt';
          break;
        case 'docx':
        case 'pdf':
          showToastNotification(`${format.toUpperCase()} export is coming soon!`);
          setIsExportOpen(false);
          return;
        default:
          console.error('Unknown format:', format);
          showToastNotification('Unsupported export format');
          return;
      }

	      const filename = buildExportFilename(
	        getDisplayName(transcription),
	        extension,
	        transcription?.display_name_user_set !== true
	      );
      downloadBlob(content, filename, mimeType);
      showToastNotification(`Exported as ${format.toUpperCase()}`);
      setIsExportOpen(false);
    } catch (error) {
      console.error('Export failed:', error);
      showToastNotification('Failed to export transcript');
    }
  };

  const getDisplayName = (item?: transcriptionsApi.TranscriptionResponse | null) => {
    if (!item) {
      return `Transcription #${id?.slice(-6) || ''}`;
    }
    const rawName = item.display_name
      ?? item.file_name
      ?? item.file_original_name
      ?? `Transcription #${item.id.slice(-6)}`;
    if (item.display_name_user_set) {
      return rawName.trim() || `Transcription #${item.id.slice(-6)}`;
    }
    return stripKnownTranscriptionTitleExtension(rawName) || `Transcription #${item.id.slice(-6)}`;
  };

  const headerTitle = transcription ? (
    <EditableFilename
      value={getDisplayName(transcription)}
      maxLength={255}
      isLoading={isRenamingDisplayName}
      onSave={handleRenameDisplayName}
      textClassName="text-lg font-semibold text-slate-900 dark:text-white"
      inputClassName="text-lg font-semibold text-slate-900 dark:text-white h-9"
    />
  ) : getDisplayName();

  // Loading skeleton
  if (loading) {
    return (
      <div className="h-[calc(100vh-32px)] flex flex-col animate-in fade-in duration-500">
        <TranscriptionSkeletonHeader />
        <TranscriptionSkeletonBody />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-[calc(100vh-32px)] flex items-center justify-center">
        <Card className="p-8 max-w-md w-full text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Failed to load transcription</h2>
          <p className="text-slate-500 dark:text-zinc-400 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
            <Button onClick={() => window.location.reload()}>
              <RefreshCw className="h-4 w-4 me-2" />
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Processing state
  if (transcription && ['pending', 'queued', 'processing'].includes(transcription.status)) {
    return (
      <div className="h-[calc(100vh-32px)] flex flex-col animate-in fade-in duration-500">
        <TranscriptHeader
          fileName={headerTitle}
          status={transcription.status}
          startedAt={transcription.started_at}
          createdAt={transcription.created_at}
          progress={transcription.progress}
          onBack={handleBack}
          onShare={() => setIsShareOpen(true)}
          sourceUrl={transcription.source_url}
          onExport={() => {}}
          onRetry={handleRetryTranscription}
          isRetrying={isRetrying}
        />
        <TranscriptionSkeletonBody />
      </div>
    );
  }

  // Failed state
  if (transcription && transcription.status === 'failed') {
    return (
      <div className="h-[calc(100vh-32px)] flex items-center justify-center">
        <Card className="p-8 max-w-md w-full text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Transcription Failed</h2>
          <p className="text-slate-500 dark:text-zinc-400 mb-6">
            {transcription.error_message || 'An error occurred during transcription. Please try again.'}
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
            <Button onClick={handleRetryTranscription} disabled={isRetrying}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isRetrying ? 'animate-spin' : ''}`} />
              {isRetrying ? 'Retrying...' : 'Retry'}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Main completed view
  return (
    <div className="h-[calc(100vh-32px)] flex flex-col animate-in fade-in duration-500">
      {/* Hidden Audio Element */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onTimeUpdate={handleAudioTimeUpdate}
          onLoadedMetadata={handleAudioLoadedMetadata}
          onPlay={handleAudioPlay}
          onPause={handleAudioPause}
          onEnded={handleAudioEnded}
          onError={handleAudioError}
          preload="metadata"
        />
      )}
      {/* Header */}
      <TranscriptHeader
        fileName={headerTitle}
        processingTime={transcription?.processing_time_seconds}
        isEdited={hasTextEdits}
        isTranslationStale={isCurrentLangStale && hasTranslationContent}
        onBack={handleBack}
        onShare={() => setIsShareOpen(true)}
        sourceUrl={transcription?.source_url}
        onExport={() => setIsExportOpen(true)}
      />

      {/* Main Content - Two Column Layout with Resizable Divider */}
      <div ref={containerRef} className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Left Column - Transcript Panel */}
        <div
          className="w-full lg:flex-none flex flex-col bg-white dark:bg-zinc-900 rounded-lg border border-slate-200 dark:border-zinc-700 shadow-sm overflow-hidden"
          style={{ flex: `0 0 ${leftPanelWidth}%` }}
        >
          {/* Toolbar */}
          <TranscriptToolbar
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            onCopyAll={() => {
              const allText = segments.map(s => s.text).join(' ');
              navigator.clipboard.writeText(allText + " \n\nTranscribed by OmniScribe.");
              showToastNotification('Transcript copied to clipboard');
            }}
            showSpeakerButton={hasSpeakerInformation}
            isSpeakerSettingsOpen={isManageSpeakersOpen}
            onToggleSpeakerSettingsOpen={() => {
              setIsTranslationOpen(false);
              setIsManageSpeakersOpen((prev) => !prev);
            }}
            speakerGroups={speakerGroups}
            onRenameSpeaker={handleRenameSpeakerGroup}
            isEdited={isEdited}
            isReverting={isReverting}
            onRevert={() => setShowRevertConfirm(true)}
            isTranslationOpen={isTranslationOpen}
            onToggleTranslationOpen={() => {
              setIsManageSpeakersOpen(false);
              setIsTranslationOpen((prev) => !prev);
            }}
            translationSettings={{
              targetLanguage,
              onTargetLanguageChange: setTargetLanguage,
              displayMode,
              onDisplayModeChange: (mode) => {
                userChangedDisplayModeRef.current = true;
                setDisplayMode(mode);
              },
              onTranslate: handleTranslate,
              isTranslating,
              translationProgress,
              hasTranslation: hasTranslationForCurrentLang,
              isTranslationStale: isCurrentLangStale,
              errorMessage: translationError,
              languageOptions: LANGUAGE_OPTIONS,
            }}
            matchCount={searchMatches.length}
            currentMatchIndex={currentMatchIndex}
            onNextMatch={handleNextMatch}
            onPrevMatch={handlePrevMatch}
            onClearSearch={handleClearSearch}
          />

          {/* Quality Rating */}
          {showRating && (
            <QualityRating onDismiss={() => setShowRating(false)} />
          )}

          {/* Segments List */}
          <div className="relative flex-1 min-h-0">
            <div ref={segmentsListRef} className="absolute inset-0 overflow-y-auto custom-scrollbar">
              {segments.length === 0 ? (
                <div className="flex items-center justify-center h-full text-slate-400 dark:text-zinc-500">
                  <div className="text-center">
                    <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No transcript segments available</p>
                  </div>
                </div>
              ) : (
                <div
                  className="w-full"
                  style={{
                    height: segmentsVirtualizer.getTotalSize(),
                    position: 'relative',
                  }}
                >
                  {segmentsVirtualizer.getVirtualItems().map((virtualRow) => {
                    const segment = segments[virtualRow.index];
                    if (!segment) return null;
                    const handlers = segmentEventHandlers.get(segment.id);
                    if (!handlers) return null;

                    const segmentMatches = matchesBySegmentId.get(segment.id) ?? EMPTY_SEARCH_MATCHES;
                    const segmentSearchQuery = segmentMatches.length > 0 ? searchQuery : '';
                    const segmentCurrentMatch = currentMatch?.segmentId === segment.id ? currentMatch : null;
                    const isSegmentEditing = editingSegmentId === segment.id;
                    const speakerName = getSpeakerDisplayName(segment);

                    return (
                      <div
                        key={segment.id}
                        data-index={virtualRow.index}
                        ref={segmentsVirtualizer.measureElement}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        <EditableSegment
                          segment={segment}
                          speakerNameOverride={speakerName}
                          isSpeakerEditing={speakerEditSegmentId === segment.id}
                          speakerMatchCount={speakerEditSegmentId === segment.id ? speakerEditMatchCount : 0}
                          isActive={activeSegment === segment.id}
                          isEditing={isSegmentEditing}
                          editingText={isSegmentEditing ? editingText : ''}
                          onTextChange={setEditingText}
                          onEdit={handlers.onEdit}
                          onSave={handlers.onSave}
                          onCancel={handleCancelEdit}
                          onClick={handlers.onSelect}
                          onPlay={handlers.onPlay}
                          onEditSpeaker={openSpeakerEdit}
                          onSaveSpeaker={handleSaveSpeaker}
                          onCancelSpeaker={closeSpeakerEdit}
                          isSavingSpeaker={isSavingSpeaker && speakerEditSegmentId === segment.id}
                          formatTime={formatSegmentTime}
                          isSaving={isSaving && editingSegmentId === segment.id}
                          displayMode={effectiveDisplayMode}
                          searchQuery={segmentSearchQuery}
                          segmentMatches={segmentMatches}
                          currentMatch={segmentCurrentMatch}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <TranscriptScrollFab
              isScrollable={isScrollable}
              isAtBottom={isAtBottom}
              onScrollToTop={handleScrollToTop}
              onScrollToBottom={handleScrollToBottom}
            />
          </div>

          {/* Upgrade Banner - show only when no subscription and not loading */}
          {!subscriptionLoading && !subscription && showUpgradeBanner && (
            <UpgradeBanner onDismiss={() => setShowUpgradeBanner(false)} />
          )}

          {/* Audio Player Bar */}
          <AudioPlayerBar
            isPlaying={isPlaying}
            currentTime={currentTime}
            duration={audioDuration || transcription?.duration || 0}
            playbackSpeed={playbackSpeed}
            volume={volume}
            disabled={!audioUrl}
            onPlayPause={handlePlayPause}
            onSkipBack={handleSkipBack}
            onSkipForward={handleSkipForward}
            onSeek={handleSeek}
            onSpeedChange={handleSpeedChange}
            onVolumeChange={handleVolumeChange}
          />
        </div>

        {/* Resizable Divider */}
        <PanelResizer onResize={handlePanelResize} />

        {/* Right Column - AI Insights Panel */}
        <div
          className="w-full min-h-[300px] lg:min-h-0 lg:flex-1"
        >
          {aiInsightsPanel}
        </div>
      </div>

      {/* Share Dialog */}
      <ShareDialog
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        transcriptionId={id!}
      />

      {/* Export Modal */}
      <Modal isOpen={isExportOpen} onClose={() => setIsExportOpen(false)} title={t('transcription.export.title')}>
        <ExportTranscript
          onExport={handleExportSubmit}
          onCancel={() => setIsExportOpen(false)}
        />
      </Modal>

      {/* Revert Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showRevertConfirm}
        onClose={() => setShowRevertConfirm(false)}
        onConfirm={handleRevertTranscription}
        title={t('transcription.toolbar.revertAllEditsTitle')}
        message={t('transcription.toolbar.revertAllEditsMessage')}
        confirmText={t('transcription.toolbar.revertConfirm')}
        variant="danger"
        isLoading={isReverting}
      />

      {/* Toast */}
      <Toast message={toastMessage} isVisible={showToast} />
    </div>
  );
}

export default TranscriptionDetail;
